import { Modal } from '@/components/Modal';
import type { PaperTab } from './paperDisplay';
import type { BandConfig } from './bandTypes';
import { useBandStatus, useSwingStatus } from './usePaper';
import { SWING_GLOSSARY, swingAiReviewSection, swingSections, swingSettings } from './swingHelp';
import { AI_REVIEW_DAYS, useAiReview } from './useAiReview';
import { PROFIT_GLOSSARY_MEANING } from './profitHelp';

/**
 * 프로필 도움말 모달. (2026-08-28 사용자 요청, 2026-08-30 전면 보강)
 *
 * "주식을 입문하는 사람 입장에서도 이해하기 쉽게" - 각 계좌가 어떻게
 * 설계되었고 어떤 기준으로 사고파는지를 전문용어 없이 설명한다.
 *
 * 🔴 세 가지를 반드시 담는다 (2026-08-30 사용자 지시)
 *   ① 지금 어떤 값으로 돌고 있나 - settings 표. 이것이 없으면 사용자가
 *      "왜 이 종목은 안 사지?" 를 스스로 판단할 수 없다
 *   ② 전문 용어는 그 자리에서 푼다 - glossary. 용어를 모르면 화면이
 *      무슨 말을 하는지 알 수 없고, 그러면 숫자를 그냥 믿게 된다
 *   ③ 화면에 실제로 있는 것만 설명한다 - 없어진 UI 를 설명하면 거짓말이 된다
 *
 * 🔴 내용의 정본은 resources/config/trading.yml 과 docs/01-trading.md 8.2·8.3절,
 *   impl/84·85·90 노트다. 설정값이 바뀌면 settings 표를 같은 커밋에서 고친다.
 *   화면 문구가 실제 동작과 다르면 도움말이 거짓말이 된다.
 *
 * 2026-09-13: 분할익절의 "먼저 참고하라" 절을 정정(필터 전 숫자·계열 안 비교였다),
 *   저점매수 계열 근거를 지금 설정 측정(114)으로, 비교 탭에 다섯 계좌 교차 비교를 넣었다
 * 2026-09-12: 분할익절에 "왜 이 계좌를 먼저 참고하나" 절, 손익 용어 풀이를 profitHelp.ts 로
 *   통합, 저점매수 계열 본문의 감시 대상 수(50→100)를 서버 설정에서 넣도록 함수화,
 *   비교 탭에 "세로 점선 — 규칙이 바뀐 날" 설명 추가.
 */

interface Setting {
  label: string;
  value: string;
}

interface Section {
  heading: string;
  body: string;
}

interface HelpContent {
  title: string;
  /** 한 문장 요약. 이것만 읽어도 성격이 잡히게 */
  oneLiner: string;
  /**
   * 지금 이 계좌가 돌아가는 값.
   *
   * 🔴 저점매수만 서버 응답에서 만든다 (아래 bandSettings)
   *   그쪽은 값을 자주 손보고 곧 설정이 다른 계좌가 하나 더 붙는다.
   *   나머지 셋은 여기 적힌 값이 정본이 아니라 **사본**이다 -
   *   config/trading.yml 의 paper.profiles 를 고치면 여기도 같이 고쳐야 한다.
   */
  settings: Setting[];
  /**
   * 본문.
   *
   * 🔴 저점매수 계열은 sectionsFor 가 있으면 화면은 그쪽을 쓴다 (아래 참조).
   *   여기에는 설정을 모를 때의 본문(sectionsFor(undefined))을 둔다 -
   *   테스트처럼 서버 없이 정적으로 읽는 곳이 있어서다.
   */
  sections: Section[];
  /**
   * 서버 설정(BandConfig)을 본문에 넣어 다시 만든다. 저점매수 계열만 갖는다.
   *
   * 🔴 본문에도 숫자를 하드코딩하지 않는다 (2026-09-12)
   *   settings 표는 2026-08-30 부터 서버 값으로 채웠는데 본문에는 "50종목" 이
   *   그대로 박혀 있었다. 감시 대상이 50→100 으로 바뀌면서(band.universe-size,
   *   다음 재선정부터) 그 문장이 곧 거짓말이 될 참이라, 본문도 설정을 받아 만든다.
   */
  sectionsFor?: (config: BandConfig | null | undefined) => Section[];
  /** 본문에 나온 전문 용어 풀이. 주식 비전문가가 읽는다 */
  glossary: { term: string; meaning: string }[];
}

/**
 * 저점매수 설정 표를 서버 응답으로 만든다.
 *
 * 🔴 화면에 숫자를 하드코딩하지 않는다 (2026-08-30 사용자 지시)
 *   도움말에 값을 박아 두면 yml 을 고쳤을 때 조용히 어긋난다. 실제로 비교 탭
 *   도움말이 이미 없어진 화면(월 목표 카드)을 계속 설명하고 있었다.
 *   설정을 바꾸는 사람이 화면 문구까지 기억해야 하는 구조는 언젠가 반드시 깨진다.
 */
export function bandSettings(config: BandConfig | null | undefined): Setting[] {
  // 아직 안 왔거나(undefined) 계좌가 없을 때(null) 둘 다 여기로 온다
  if (config === undefined || config === null) {
    return [{ label: '설정', value: '불러오는 중…' }];
  }
  // 거래대금은 원 단위로 와서 그대로 쓰면 0 이 열한 개다. 억으로 줄인다
  const tradeAmountEok = Math.round(config.minTradeAmount / 100_000_000);
  // 한 달을 21거래일로 본다. 120거래일 → 약 6개월
  const months = Math.round(config.bandWindow / 21);

  return [
    { label: '감시 대상', value: `왕복성 상위 ${config.universeSize}종목` },
    // 챌린저만 - 보유 상한 8 자리를 순위 낮은 종목이 먼저 차지하지 않게 (2026-09-19 I-62)
    ...(config.maxSwingRank !== null && config.maxSwingRank < config.universeSize
      ? [{ label: '새로 사는 종목', value: `그중 왕복성 상위 ${config.maxSwingRank}종목만` }]
      : []),
    {
      label: '거래대금 하한',
      value: `하루 평균 ${tradeAmountEok.toLocaleString('ko-KR')}억원 이상`,
    },
    { label: '대상 갱신', value: `${config.universeRefreshDays}일마다 다시 뽑음` },
    { label: '밴드 기간(윈도)', value: `${config.bandWindow}거래일 (약 ${months}개월)` },
    { label: '매수 기준가', value: `그 기간 가격의 하위 ${config.buyPercentile}% 분위` },
    {
      label: '분할매수',
      // 챌린저는 1단 - "최대 1번 나눠" 는 말이 안 된다 (2026-09-19 I-59)
      value:
        config.maxRungs <= 1
          ? '나눠 사지 않음 - 첫 매수에 종목당 한도를 한 번에'
          : `최대 ${config.maxRungs}번 · ${config.rungAtrMultiple} ATR 씩 더 내려올 때마다`,
    },
    { label: '종목당 한도', value: `총자산의 ${config.perStockRatio}%` },
    ...(config.maxPositions !== null
      ? [{ label: '보유 상한', value: `동시에 최대 ${config.maxPositions}종목` }]
      : []),
    { label: '익절', value: `평균 매수가 +${config.bouncePct}% (전량)` },
    // 이미 손절선 아래면 사지 않는다 - 사자마자 파는 매수다 (2026-09-19 I-62)
    { label: '손절', value: `매수 기준가 −${config.stopAtrMultiple} ATR (이미 그 아래면 사지 않음)` },
    { label: '재매수 금지', value: `손절한 종목은 ${config.stopCooldownDays}일간` },
    { label: '꺼지는 업종 회피', value: momentumText(config) },
  ];
}

/**
 * 🇺🇸 미국 저점매수 설정 표. (2026-10-10 · 사용자 질문 "미국 저점매수는 ? 도움말이 안 보인다")
 *
 * bandSettings 를 그대로 쓰지 않는 이유 — 거래대금 하한이 **달러**라 "억원" 으로 바꾸면 틀린 숫자가 된다.
 * 🔴 매수 분위가 0 이면 "아직 꺼짐" 이라고 적는다 — ✏️ 10/11 설정은 0.20 이고, 4회차 전엔 서버가 0 으로 싣는다(BandRoundGate · band.round-rules.us-p20-from-round).
 *   "하위 0% 분위" 로 보이면 이상한 규칙으로 읽힌다.
 */
export function usBandSettings(config: BandConfig | null | undefined): Setting[] {
  if (config === undefined || config === null) {
    return [{ label: '설정', value: '불러오는 중…' }];
  }
  const off = config.buyPercentile <= 0;
  const amountManDollar = Math.round(config.minTradeAmount / 10_000);
  return [
    { label: '상태', value: off ? '아직 꺼짐 — 4회차를 열면 저절로 켜집니다(그 전엔 사지 않음)' : '켜짐' },
    { label: '감시 대상', value: `미리 정한 미국 종목 중 왕복성 상위 ${config.universeSize}종목` },
    { label: '거래대금 하한', value: `하루 평균 $${amountManDollar.toLocaleString('ko-KR')}만 이상` },
    { label: '밴드 기간(윈도)', value: `${config.bandWindow}거래일` },
    { label: '매수 기준가', value: off ? '꺼짐(켜면 하위 20% 분위)' : `그 기간 가격의 하위 ${config.buyPercentile}% 분위` },
    {
      label: '분할매수',
      value:
        config.maxRungs <= 1
          ? '나눠 사지 않음'
          : `최대 ${config.maxRungs}번 · ${config.rungAtrMultiple} ATR 씩 더 내려올 때마다`,
    },
    { label: '종목당 한도', value: `총자산의 ${config.perStockRatio}%` },
    { label: '익절', value: `평균 매수가 +${config.bouncePct}% (전량)` },
    { label: '손절', value: `매수 기준가 −${config.stopAtrMultiple} ATR (이미 그 아래면 사지 않음)` },
    { label: '재매수 금지', value: `손절한 종목은 ${config.stopCooldownDays}일간` },
    { label: '돈 · 시각', value: '원화 2,000만원 계좌 · 살 때 · 팔 때 그 순간 환율로 바꿈 · 미국 정규장(한국 시각 밤)에만 매매' },
  ];
}

/** 🇺🇸 미국 저점매수 본문 — 규칙 값은 위 설정 표를 가리킨다 */
function usBandSections(config: BandConfig | null | undefined): Section[] {
  const off = config !== undefined && config !== null && config.buyPercentile <= 0;
  return [
    {
      heading: '지금 상태',
      body: off
        ? '계좌와 장부는 만들어 두었지만 아직 사지 않습니다. 4회차를 열면 저절로 켜져 그날 밤 미국 장부터 삽니다.'
        : '켜져 있습니다. 한국 시각 밤 미국 정규장 동안 1분마다 감시해 사고팝니다.',
    },
    {
      heading: '어떤 규칙인가',
      body:
        '국내 저점매수(보수)와 같은 방식을 미국 종목에 옮겼습니다 — 늘 오르내리는 종목을 정해 두고, ' +
        '최근 기간 가격 폭의 아래쪽에 오면 나눠 사고, 평균 매수가에서 정해 둔 만큼 오르면 팝니다. ' +
        '규칙 값은 2026-09-27 측정 조건 그대로 동결했습니다 — 그 뒤 국내 계좌에서 바뀐 칸(손절 등)이 있어 지금 국내 값과 다른 곳이 있고, 이 계좌의 값은 위 설정 표에 있습니다. 미국 과거 데이터로 수익을 재서 고른 값이 아닙니다 — ' +
        '지금 고른 미국 종목들은 이미 잘된 회사들이라 과거 수익이 부풀려 보이기 때문입니다.',
    },
    {
      heading: '돈은 어떻게 세나',
      body:
        '계좌는 원화입니다. 종목을 살 때와 팔 때 그 순간의 원/달러 환율로 바꿉니다. 그래서 손익이 두 가지입니다 — ' +
        '달러로 번 것(종목 매매의 결과)과 원화로 남은 것(환율 몫까지 포함). 둘이 다르면 그 차이가 환율 몫입니다.',
    },
  ];
}

/** 섹터 모멘텀 필터 설정 한 줄. 꺼져 있으면 그렇다고 적는다. */
function momentumText(config: BandConfig): string {
  const window = config.sectorMomentumWindow ?? 0;
  const bottomN = config.sectorMomentumBottomN ?? 0;
  if (window <= 0 || bottomN <= 0) {
    return '사용 안 함';
  }
  return `최근 ${window}거래일 성적 하위 ${bottomN}개 업종은 안 삼`;
}

// ── 용어 풀이 (여러 계좌가 함께 쓴다) ──

const G_ATR = {
  term: 'ATR',
  meaning:
    '이 종목이 하루에 보통 얼마나 움직이는지를 나타내는 값입니다. 많이 움직이는 종목은 ' +
    '목표와 손절도 그만큼 멀리 잡혀야 공평해서, 금액이 아니라 ATR 배수로 선을 긋습니다. ' +
    '예를 들어 ATR가 2,000원인 종목에서 "+1.5 ATR"은 기준 가격(추천 계좌는 추천가)보다 3,000원 위를 뜻합니다.',
};

const G_CUT = {
  term: '익절 · 손절',
  meaning:
    '익절은 이익이 났을 때 그것을 확정하려고 파는 것이고, 손절은 손실이 더 커지기 전에 ' +
    '파는 것입니다. 두 선을 미리 정해 두면 값이 움직일 때마다 고민하지 않아도 됩니다.',
};

/*
  🔴 2026-08-31 사용자 질문 - "손익은 무엇이고 실현손익은 뭐야?"
    비교 표에 두 값이 나란히 있는데 어디에도 설명이 없었다.
    둘의 차이가 곧 "아직 안 판 종목의 평가"라서, 그걸 모르면
    장부상 이익을 확정 이익으로 읽게 된다.

  🔴 문구는 profitHelp.ts 가 정본이다 (2026-09-12)
    같은 설명이 계좌 카드·청산 목록·비교 표의 ? 에도 붙는다. 여기 따로 적어 두면
    한쪽만 고쳤을 때 같은 말을 두 가지로 설명하게 된다 (CLAUDE.md 5.2절).
*/
const G_PROFIT = {
  term: '손익 · 실현 손익 · 평가 손익',
  meaning: PROFIT_GLOSSARY_MEANING,
};

/*
  🔴 같은 질문 - "반등익절, 구조이탈은 무슨 뜻이지?"
    저점매수 청산 목록의 배지에 쓰이는 말인데 설명이 없었다.
    특히 "구조 이탈"은 이 계좌의 전제를 알아야 이해되는 말이다.
*/
const G_BAND_EXIT = {
  term: '반등 익절 · 구조 이탈',
  meaning:
    // 🔴 계좌별 값(익절폭 · ATR 배수 · 재매수 금지 일수)을 적지 않는다 (2026-09-29 I-10)
    //   용어 풀이는 세 탭이 같이 쓰는 고정 문구라 설정을 못 받는다 — 값은 위 설정 표를 가리킨다
    '저점매수가 파는 두 가지 이유입니다. 반등 익절은 평균 매수가에서 계획한 만큼 올라 ' +
    '판 것입니다 — 그 폭은 계좌마다 다르고 위 설정 표의 "익절" 에 있습니다. ' +
    '구조 이탈은 매수 기준가보다 하루 평균 변동폭(ATR)의 몇 배나 더 아래로 뚫고 내려가 손절한 것입니다 — ' +
    '몇 배인지는 위 설정 표의 "손절" 에 있습니다(퍼센트가 아니라 그 종목이 하루에 움직이는 폭의 배수라 종목마다 다릅니다). ' +
    '이 계좌는 "이 종목은 일정 범위를 오간다"를 ' +
    '전제로 사는데, 그만큼 빠졌다는 건 그 전제가 틀렸다는 신호입니다. 오르내리는 게 ' +
    '아니라 그냥 내려가는 중인 것입니다. 그래서 그 종목은 한동안(위 설정 표의 "재매수 금지") 다시 사지 않습니다 — ' +
    '무너진 자리는 하단이 계속 내려가서, 다시 들어가면 떨어지는 칼을 반복해 잡게 됩니다. ' +
    '청산 목록에는 회색 "회차 종료" 도 있습니다 — 회차 마지막 날 들고 있던 것을 한꺼번에 정리한 것이라 ' +
    '매매 판단이 아니라 기간 때문에 판 것입니다. 2회차까지만 있고, 3회차부터는 회차가 끝나도 팔지 않고 ' +
    '다음 회차로 넘깁니다 — 넘어간 종목은 산 때의 익절 · 손절 규칙 그대로 끝냅니다.',
};

const G_BACKTEST = {
  term: '백테스트',
  meaning:
    '과거 데이터에 이 규칙을 그대로 돌려보고 결과를 세는 것입니다. 실제로 매매한 기록이 ' +
    '아니라 시뮬레이션이며, 과거에 통했다고 앞으로도 통한다는 뜻은 아닙니다.',
};

const G_DRAWDOWN = {
  term: '최대 하락폭',
  meaning:
    '그 기간 중 계좌가 가장 높았던 시점에서 가장 많이 줄어들었을 때의 폭입니다. ' +
    '-26%라면 한때 가장 높았던 때보다 4분의 1이 넘게 줄어든 적이 있다는 뜻입니다. ' +
    '수익률이 같아도 이 값이 크면 중간에 견디기 훨씬 어렵습니다.',
};

const G_SCORE = {
  term: '추천 점수',
  meaning:
    '변동성·눌린 정도·거래량 같은 조건을 각각 점수로 바꿔 합한 값입니다(0~100). ' +
    '그날 종목들 사이의 순위가 아니라 절대 기준이라, 조건에 맞는 종목이 없는 날은 ' +
    '추천이 0건입니다. 기준을 낮춰 억지로 채우지 않습니다.',
};

// ── 저점매수 전용 용어 ──

const G_SWING = {
  term: '왕복성',
  meaning:
    '고점과 저점 사이를 얼마나 자주 오갔는지를 재는 값입니다. 이 값이 높은 종목은 ' +
    '한 방향으로 쭉 가기보다 오르내림을 반복해서, 싸질 때 사고 오르면 파는 방식이 맞습니다.',
};

const G_WINDOW = {
  term: '밴드 기간(윈도)',
  meaning:
    '매수 기준가를 계산할 때 며칠치 가격을 보는지입니다. 120거래일이면 약 6개월치를 봅니다. ' +
    '길게 볼수록 기준가가 천천히 움직이고, 짧게 볼수록 최근 가격을 빨리 따라갑니다. ' +
    '짧게 보면 더 자주 살 수 있지만 계좌가 출렁이는 폭도 커집니다.',
};

const G_PERCENTILE = {
  term: '분위(하위 N%)',
  meaning:
    // 🔴 예시로만 숫자를 쓴다 — 어느 계좌가 몇 % 인지는 적지 않는다 (2026-09-29 I-10 · 위 설정 표의 "매수 기준가")
    '그 기간의 가격을 싼 순서로 줄 세웠을 때 아래에서 N%에 해당하는 가격입니다. ' +
    '예를 들어 하위 20%는 "꽤 싸졌을 때", 하위 40%는 "조금 싸졌을 때", 하위 50%는 "중간까지 내려왔을 때"를 뜻합니다. ' +
    '숫자가 클수록 더 자주 사게 되고, 작을수록 더 깊이 기다렸다 삽니다. 이 계좌의 값은 위 설정 표의 "매수 기준가" 에 있습니다.',
};

const G_MOMENTUM = {
  term: '섹터 모멘텀',
  meaning:
    '한 업종에 속한 종목들의 최근 성적을 평균 낸 값입니다. 이 값이 낮다는 것은 ' +
    '그 업종이 통째로 밀리고 있다는 뜻입니다. 시스템은 뉴스를 읽지 못하지만, ' +
    '업종이 함께 내리는 것은 볼 수 있습니다 — 그래서 성적이 가장 나쁜 몇 개 업종의 ' +
    '종목은 아무리 싸 보여도 사지 않습니다. 값이 싸진 이유가 그 업종에 나쁜 일이 ' +
    '생겨서일 수 있기 때문입니다.',
};

const G_LADDER = {
  term: '분할매수(사다리)',
  meaning:
    '한 번에 다 사지 않고 값이 더 떨어질 때마다 나눠 사는 방식입니다. 평균 매수가가 ' +
    '낮아져 조금만 반등해도 이익이 나지만, 계속 떨어지는 종목이면 손실도 함께 커집니다. ' +
    '그래서 횟수(3번)와 종목당 금액(20%)에 상한을 둡니다.',
};

// ── 저점매수 계열 본문 (서버 설정을 받아 만든다) ──

/**
 * 2026-08-28 관측 당시의 감시 대상 수.
 *
 * 🔴 날짜가 붙은 과거 사실은 숫자를 고쳐 쓰지 않는다
 *   "2026-08-28 기준 50종목 중 7종목이 매수권" 은 그날의 관측이라, 감시 대상이
 *   100으로 바뀌어도 "100종목 중 7종목" 이었던 적은 없다. 숫자는 그대로 두고
 *   그때의 대상 수를 옆에 밝혀, 지금 값(설정 표)과 섞여 거짓말이 되지 않게 한다.
 */
const UNIVERSE_SIZE_ON_2026_08_28 = 50;

/**
 * "종목 N개를 골라" 자리. 설정이 아직 안 왔거나(undefined) 계좌가 없으면(null)
 * 수를 말하지 않는다 - 빈 자리에 옛 값을 두면 그것이 곧 거짓말이 된다.
 */
function universePhrase(config: BandConfig | null | undefined): string {
  if (config === undefined || config === null) {
    return '종목을 정해진 수만큼(위 설정 표의 감시 대상) 골라';
  }
  return `종목 ${config.universeSize}개를 골라`;
}

/**
 * 본문의 "지금 규칙" 문장에 넣을 값. (2026-09-29 BACKLOG I-10)
 *
 * 🔴 왜 — 설정 표(bandSettings)는 2026-08-30 부터 서버 값인데 본문에는 300억 · 120거래일 · 하위 20% · +3% ·
 *   1 ATR · 3번 · 20% · 1.5 ATR · 28일이 손으로 옮긴 사본으로 남아 있었다. trading.yml 을 고치면 표와 본문이 갈린다
 *   (9/28 I-78 2차에서 그런 곳을 한꺼번에 고친 것과 같은 병).
 * 🔴 날짜가 붙은 과거 사실("2026-09-21 에 2 → 1.5 ATR 로 당겼다")은 여기서 받지 않는다 — 그날의 값이 맞다(UNIVERSE_SIZE_ON_2026_08_28 원칙)
 * 설정이 아직 안 왔으면 숫자 대신 "위 설정 표" 를 가리킨다 — 빈 자리에 옛 값을 두면 곧 거짓말이 된다(universePhrase 와 같다)
 */
function ruleWords(config: BandConfig | null | undefined) {
  if (config === undefined || config === null) {
    return {
      amount: '일정 금액(위 설정 표)',
      window: '일정 기간(위 설정 표)',
      percentile: '아래쪽 일정 분위(위 설정 표)',
      rungStep: '일정 간격(위 설정 표)',
      rungs: '정해진 횟수',
      perStock: '정해진 비율(위 설정 표)',
      bounce: '정해진 폭(위 설정 표)',
      stop: '정해진 폭(위 설정 표의 ATR 배수)',
      stopShort: '위 설정 표의 손절폭',
      cooldown: '정해진 기간(위 설정 표) 동안',
      maxPositions: '정해진 수(위 설정 표)',
      maxSwingRank: '정해진 순위(위 설정 표)',
    };
  }
  const eok = Math.round(config.minTradeAmount / 100_000_000).toLocaleString('ko-KR');
  return {
    amount: `${eok}억`,
    window: `${config.bandWindow}거래일(약 ${Math.round(config.bandWindow / 21)}개월)`,
    percentile: `하위 ${config.buyPercentile}%`,
    rungStep: `${config.rungAtrMultiple} ATR`,
    rungs: `최대 ${config.maxRungs}번`,
    perStock: `${config.perStockRatio}%`,
    bounce: `+${config.bouncePct}%`,
    stop: `${config.stopAtrMultiple} ATR(그 종목 하루 평균 변동폭의 ${config.stopAtrMultiple}배)`,
    stopShort: `${config.stopAtrMultiple} ATR`,
    cooldown: `${config.stopCooldownDays}일간`,
    maxPositions: config.maxPositions === null ? '제한 없이' : `${config.maxPositions}종목`,
    maxSwingRank: config.maxSwingRank === null ? '감시 대상 전부' : `상위 ${config.maxSwingRank}종목`,
  };
}

/**
 * 2026-08-28 관측 문장 뒤에 붙는 꼬리표.
 * "그때는 감시 대상이 50종목이었다" 를 밝히고, 설정이 있으면 지금 값을 나란히 적는다.
 */
function universeThenNote(config: BandConfig | null | undefined): string {
  const then = `그때는 감시 대상이 ${UNIVERSE_SIZE_ON_2026_08_28}종목이었습니다`;
  if (config === undefined || config === null) {
    return `(${then})`;
  }
  return `(${then}. 지금 설정은 ${config.universeSize}종목입니다)`;
}

/**
 * 저점매수 두 계좌 공통 - 재봤지만 넣지 않은 규칙. (2026-09-14 사용자 요청)
 *
 * "기각 사유를 도움말에 간단히 남겨달라." 좋아 보이는 아이디어를 왜 안 넣었는지 화면에
 * 없으면 가족이 같은 제안을 다시 하게 된다. 두 계좌가 같은 측정을 같은 이유로 기각했으므로
 * 한 곳에 두고 둘 다 쓴다(CLAUDE.md 5.2).
 *
 * 🔴 숫자는 docs/impl/114 10절(갭 가드·코스닥)과 5절 (b)(시장 하락 가드)의 사본이다.
 *   다시 재면 여기도 같이 고친다.
 */
const BAND_REJECTED_SECTION: Section = {
  heading: '재봤지만 넣지 않은 것 (2026-09-14)',
  body:
    '좋아 보이는 아이디어도 과거 2년치를 앞뒤 두 기간으로 갈라 재서, 두 기간 모두 나아야만 넣습니다. ' +
    '① 코스닥 종목도 감시하기 — 코스닥의 작은 테마주들이 감시 목록을 채우면서 뒤 기간 수익이 ' +
    '저점매수 +159.5% → +92.0%, 저점매수(리스크) +171.3% → +100.1%로 줄었고, 최대 낙폭은 ' +
    '−21.3% → −38.2%, −24.6% → −47.8%로 거의 두 배가 됐습니다. 그래서 코스피 종목만 봅니다. ' +
    '② 갭하락으로 시작한 날은 사지 않기 — 시장 전체가 1~2% 아래에서 시작한 날, 또는 그 종목이 ' +
    '3~5% 아래에서 시작한 날을 건너뛰게 해봤습니다. 거의 모든 경우 수익이 줄었고, 종목이 갭하락한 날을 ' +
    '거르면 수익이 3분의 1 가까이 깎였습니다. 갭하락으로 시작한 날이 오히려 기준가보다 싸게 사는 ' +
    '날이었기 때문입니다. 시장이 2% 넘게 빠져 시작한 날만 거르는 방식이 저점매수(리스크)에서 조금 ' +
    '나았지만, 2년 중 4일뿐이라 우연과 가릴 수 없어 넣지 않았습니다. ' +
    '③ 같은 이유로 "시장이 며칠 빠지면 쉬기"(2026-09-12)도 넣지 않았습니다. ' +
    '떨어진 날에 사는 계좌라, 떨어진 날을 피하면 사야 할 자리를 막게 됩니다.',
};

/**
 * 저점매수 본문. (2026-09-12 함수화)
 *
 * ✏️ 2026-09-29(I-10) "지금 규칙" 문장의 숫자는 전부 설정에서 받는다(ruleWords). 날짜가 붙은 과거 사실만 그대로 둔다.
 *   예전 주석 — "설정에서 받는 것은 감시 대상 수뿐 · 300억 · 120거래일 · … · 28일은 사본" — 은 이 커밋으로 끝났다
 */
export function bandSections(config: BandConfig | null | undefined): Section[] {
  const w = ruleWords(config);
  return [
    {
      heading: '어떤 종목을 보나',
      body:
        `고점과 저점을 자주 오간(왕복성이 높은) ${universePhrase(config)} 한 달마다 갱신합니다. ` +
        `하루 평균 ${w.amount} 이상 거래되는 종목만 대상이라 대형주와 중형주가 함께 들어옵니다. ` +
        'SK하이닉스·효성중공업·LS일렉트릭처럼 변동은 있지만 망하지는 않을 종목들이 ' +
        '자연히 뽑힙니다. 새 종목을 발굴하지 않고 늘 보던 종목만 반복해서 매매합니다. ' +
        '감시 대상 수를 바꾸면 다음 갱신 때부터 그 수로 뽑힙니다(2026-09-12 에 50→100).',
    },
    {
      heading: '언제 사나 (분할 매수)',
      body:
        `종목마다 최근 ${w.window} 가격의 ${w.percentile} 지점을 "매수 기준가"로 잡습니다. ` +
        `현재가가 기준가 아래로 내려오면 1차로 사고, 거기서 ${w.rungStep}씩 더 떨어질 때마다 ` +
        `${w.rungs}까지 나눠 삽니다. "하이닉스가 130~140에 오면 산다. 더 떨어지면 ` +
        `더 산다"를 그대로 옮긴 규칙입니다. 종목 하나에 계좌의 ${w.perStock}까지만 씁니다.`,
    },
    {
      heading: '언제 파나',
      body:
        `평균 매수가에서 ${w.bounce} 반등하면 전량 팝니다. 그리고 다시 내려오면 또 삽니다 - ` +
        `이 반복이 이 계좌의 전부입니다. 단, 기준가보다 ${w.stop} ` +
        `아래로 뚫고 내려가면 "오르내림이 깨졌다"고 보고 손절하며, 그 종목은 ${w.cooldown} 다시 사지 않습니다. ` +
        '이 손절이 없으면 끝없이 떨어지는 종목을 계속 사게 됩니다. ' +
        '이미 그 손절선 아래 가격이면 처음부터 사지 않습니다(사자마자 팔게 되는 매수라서).',
    },
    /*
      🔴 2026-09-20 사용자 결정 - 손절 2 → 1.5 ATR (3회차 9/21 시작부터, 112번 36-1b · 37절)
        이 계좌는 사용자 원래 규칙(하위 20% · +3% · 2 ATR)을 그대로 따라가던 기준 계좌였다.
        바꾸면서 "원래 규칙이 실전에서 어떤가" 는 더 볼 수 없게 된 것을 사용자가 알고 정했다.
    */
    {
      heading: '왜 손절을 1.5 ATR로 당겼나 (2026-09-21 변경)',
      body:
        '2회차가 끝나고 "회차 끝에 손실로 물려 있는 종목을 줄일 방법"을 계좌 단위로 재봤습니다 — ' +
        '오래 들고 있으면 파는 것, 손절을 당기는 것, 예수금을 남겨 두는 것 세 가지입니다. ' +
        '이 계좌는 손절을 2 ATR에서 1.5 ATR로 당겼을 때만 과거 2년의 앞 기간 · 뒤 기간 모두 ' +
        '수익이 오르고 계좌가 출렁이는 폭이 줄었습니다. 산 날 바로 파는 경우를 보수적으로 보든 후하게 보든 ' +
        '같았고, 두 번 따로 재서 두 번 다 같았습니다(예: 후하게 본 뒤 기간 +93.5% → +133.8%, 최대 낙폭 −16.3% → −6.2%). ' +
        '대가도 있습니다 — 손절이 약 25% 늘고, 이긴 거래 비율이 2~3%p 줄어듭니다. ' +
        '크게 물리기 전에 일찍 끊는 대신 작은 손절을 더 자주 받는 교환입니다. ' +
        '🔴 1.5%가 아닙니다. 하루에 3% 움직이는 종목이면 기준가보다 약 4.5% 아래, 8% 움직이는 종목이면 약 12% 아래입니다. ' +
        '🔴 이 계좌는 원래 사용자의 매매 방식(하위 20% · +3% · 2 ATR)을 그대로 따라가던 기준이었습니다. ' +
        '3회차부터는 손절만 달라집니다.',
    },
    /*
      🔴 2026-08-31 사용자 질문에 대한 답을 화면에 남긴다
        "2~3%만 오르면 다 팔아버리나? 5~10%까지 갈 수도 있는데."
        당연히 드는 의문이라 도움말이 미리 답하지 않으면 계속 다시 묻게 된다.
        측정 근거는 docs/impl/99 에 있다.
    */
    {
      heading: '왜 +3%에 다 파나 (더 기다리면 안 되나)',
      /*
        🔴 이 절은 [저점매수](하위 20%) 계좌의 답이다.
          [저점매수(리스크)](하위 40%)는 2026-09-10 에 같은 측정에서 다른 답이
          나와 +4% 로 갈렸고, 2026-09-21 에 +5% 가 됐다 (그쪽 탭 도움말의 "왜 이 계좌만 +5%인가" 참조).

          🔴 "리스크 계좌" 라고 줄여 쓰지 않는다
            그 이름이 붙은 계좌가 둘이다 - [하이리스크](추천 계좌, 익절이 +3.0 ATR)와
            [저점매수(리스크)](익절이 +5%, 9/21 전 +4%). 내부 식별자까지 둘 다 AGGRESSIVE 라
            (RecommendationProfile.AGGRESSIVE / BandVariant.AGGRESSIVE)
            줄여 쓰면 어느 쪽인지 알 수 없다. 실제로 그 혼동이 있었다.
      */
      body:
        '하루에 5~10% 오르는 날이 분명히 있으니 자연스러운 의문입니다. 실제로 재봤습니다 — ' +
        '절반만 팔고 나머지를 더 태우는 방식(고점 따라가기·+8%까지 기다리기·밴드 위쪽까지 ' +
        '기다리기)을 아홉 가지로, 익절 기준을 +2%부터 +12%까지 일곱 가지로 걸어 ' +
        '2년치를 앞뒤로 갈라 검증했습니다. 결과는 지금 방식(+3% 전량)이 전부 이겼고, ' +
        '수익뿐 아니라 계좌가 출렁이는 폭까지 나빠졌습니다. ' +
        '이유는 매매 횟수에 있습니다 — 절반을 남겨두면 그 돈이 그 종목에 묶여, ' +
        '다음에 다른 종목이 싸졌을 때 사지 못합니다. 이 계좌는 한 번에 크게 버는 것이 아니라 ' +
        '작게 자주 벌어 그 돈을 바로 다시 굴리는 것이 수익원입니다. ' +
        '오래 들고 있으면 그만큼 시장 흔들림을 더 맞기도 합니다. ' +
        '참고로 [분할익절] 계좌는 반대로 절반씩 나눠 파는 것이 유리했습니다 — ' +
        '그쪽은 며칠씩 들고 가는 계좌라 성격이 다릅니다. ' +
        '🔴 2026-09-19 점검에서 이 비교가 "산 날에는 팔지 못한다"는 가정으로 재졌다는 것이 드러났습니다 — ' +
        '그 가정은 좁은 익절을 유리하게 봅니다. 고쳐서 다시 재니 이 계좌는 앞 기간에는 +3%, 뒤 기간에는 +5%가 나아 ' +
        '답이 갈렸습니다. 한쪽 기간에서만 좋은 값은 고르지 않는다는 원칙대로 +3%를 그대로 둡니다.',
    },
    {
      heading: '꺼지는 업종은 건너뜁니다',
      body:
        '시스템은 뉴스를 읽지 못합니다. "미국에서 값싼 반도체가 나왔다" 같은 소식을 ' +
        '모르고, 가격이 떨어진 것만 보고 "싸졌다"며 삽니다. 그래서 업종 단위로 ' +
        '한 겹을 덧댔습니다 — 같은 업종 종목들의 최근 성적을 평균 내, 가장 밀리는 ' +
        '몇 개 업종은 아무리 싸 보여도 사지 않습니다. 나쁜 일이 생긴 업종이라면 ' +
        '그 안의 종목이 싼 데는 이유가 있기 때문입니다. ' +
        '단, 여러 업종에 걸친 종목은 하나라도 살아 있으면 삽니다.',
    },
    BAND_REJECTED_SECTION,
    {
      heading: '🔴 지금 설정은 잘 사지 않습니다',
      body:
        `2026-08-28 기준으로, 감시 중인 ${UNIVERSE_SIZE_ON_2026_08_28}종목 가운데 매수 기준가에 ` +
        `닿아 있던 것은 7종목뿐이었습니다${universeThenNote(config)}. ` +
        'SK하이닉스는 그때 시세보다 39% 더 떨어져야 살 수 있었습니다. ' +
        '밴드 기간을 6개월로 길게 잡고 하위 20%라는 깊은 자리를 기준으로 삼았기 때문입니다. ' +
        '대신 계좌가 출렁이는 폭이 가장 작습니다. ' +
        '"자주 사되 더 출렁이는" 설정은 저점매수(리스크) 계좌로 나란히 굴려 비교하고 있습니다.',
    },
    {
      heading: '근거와 그 한계',
      body:
        /*
          🔴 2026-09-13 지금 설정 측정(114번)으로 바꿨다
            이전 첫 문장(+164.1%·−26.2%·월 35회)은 옛 설정의 전체 기간 수치였다.
        */
        '지금 설정(하위 20%·+3%·감시 100종목·왕복 체결 손해 0.2%)으로 과거를 앞뒤 두 기간으로 ' +
        '나눠 검증했습니다(2026-09-12). 앞 기간 +41.8%(시장 +23.4%), 뒤 기간 +159.5%' +
        '(시장 +57.0%)로 두 기간 모두 시장을 앞섰고, 최대 낙폭은 앞 −8.3%·뒤 −21.3%로 ' +
        '그때 함께 잰 계좌들의 검증 결과 가운데 가장 작습니다. ' +
        '🔴 다만 이 수익률로 설정을 고르지는 않습니다 - 2026-08-30 검증에서 앞 기간 1등이던 ' +
        '설정이 뒤 기간 11등이었습니다. 수익률 순위는 기간이 바뀌면 뒤집히지만 ' +
        '"얼마나 출렁이는가"는 유지되어, 이 계좌의 값은 하락폭이 작다는 근거로 유지합니다. ' +
        '🔴 두 기간 모두 시장이 오르던 때라 긴 하락장에서의 모습은 알 수 없고, ' +
        '"매수 기준가에 그대로 체결된다"고 가정한 결과입니다. ' +
        '🔴 2026-09-19 점검에서 이 검증이 "산 날에는 팔지 못한다"는 가정으로 재졌다는 것이 드러났습니다. ' +
        '실제로는 산 날 오후에 목표가에 닿으면 그날 팝니다. 그 가정을 고치고 3회차 설정(손절 1.5 ATR · 회차 4주)으로 다시 재면 ' +
        '앞 +20.5% · 뒤 +22.9%(보수적으로 본 경우) ~ 앞 +35.7% · 뒤 +133.8%(후하게 본 경우)입니다. 실제는 그 사이에 있습니다.',
    },
  ];
}

/**
 * 저점매수(리스크) 본문. (2026-09-12 함수화)
 *
 * ✏️ 2026-09-29(I-10) 이 계좌의 지금 값은 설정에서 받는다(ruleWords).
 * 🔴 **다른 계좌(저점매수)의 값은 사본이다** — 탭 하나는 자기 계좌 설정만 받는다. 저점매수 값을 바꾸면 여기 "저점매수는" 줄도 본다
 */
export function bandAggressiveSections(config: BandConfig | null | undefined): Section[] {
  const w = ruleWords(config);
  return [
    {
      heading: '무엇이 다른가',
      body:
        '세 가지가 다릅니다. 첫째는 매수 기준가를 잡는 깊이입니다 — 저점매수는 ' +
        `최근 6개월 가격의 하위 20% 까지 기다리지만, 이 계좌는 ${w.percentile} 에서 삽니다. ` +
        `둘째는 익절폭입니다 — 저점매수는 +3%, 이 계좌는 ${w.bounce} 에 팝니다(2026-09-21 에 +4% → +5%). ` +
        `셋째는 손절폭입니다 — 저점매수는 1.5 ATR로 당겼고(2026-09-21), 이 계좌는 ${w.stopShort}입니다. ` +
        '감시 대상·분할매수·종목당 한도는 같습니다. ' +
        '🔴 2026-09-10 이전에는 매수 깊이 하나만 달랐습니다. 익절폭과 손절폭이 갈리면서 ' +
        '두 계좌의 성적 차이가 어느 쪽 때문인지는 이 표만으로 가릴 수 없게 됐습니다.',
    },
    {
      heading: '왜 만들었나',
      body:
        '저점매수가 거의 사지 않았기 때문입니다. 2026-08-28 기준 감시 중인 ' +
        `${UNIVERSE_SIZE_ON_2026_08_28}종목 가운데 매수 기준가에 닿은 것이 7개뿐이었고, ` +
        'SK하이닉스는 39% 더 떨어져야 살 수 있었습니다. 이 계좌는 같은 날 기준으로 ' +
        `18종목이 매수권이었습니다${universeThenNote(config)}.`,
    },
    /*
      🔴 2026-09-20 사용자 결정 - 익절 +4% → +5% (3회차 9/21 시작부터, 112번 37절)
        9/10 의 +4% 채택 근거는 "산 날에는 팔지 못하는" 하네스 위의 값이었다(112번 36절 결함 ③).
        이 절은 두 번의 변경을 순서대로 적는다 - 옛 근거를 지우면 몇 달 뒤 "+4% 가 낫다고 하지 않았나" 를 다시 묻게 된다.
    */
    {
      heading: '왜 이 계좌만 +5%인가 (2026-09-21 변경)',
      body:
        '처음에는 저점매수와 같은 +3%였고, 2026-09-10에 +4%로 올렸습니다. 익절폭을 +2%부터 +12%까지 ' +
        '일곱 가지로 걸어 2년치를 앞뒤로 갈라 재봤을 때 이 계좌(하위 40%)는 +3%가 앞 기간 1위이면서 ' +
        '뒤 기간 6위였고, +4%가 두 기간 모두 무난했기 때문입니다. ' +
        '그런데 2회차가 끝나고 점검해 보니 그 검증은 "산 날에는 팔지 못한다"고 가정하고 있었습니다. ' +
        '실제로는 산 날 오후에 목표가에 닿으면 그날 팝니다. 그 가정 때문에 좁은 익절(+3·+4%)이 유리하게 보였습니다 — ' +
        '목표를 넘긴 날의 추가 상승분을 다음 날 아침 값에 챙긴 것으로 계산됐기 때문입니다. ' +
        '고쳐서 다시 재니 +5%가 +4%보다 앞 기간 · 뒤 기간, 산 날 매도를 보수적으로 본 경우 · 후하게 본 경우 네 가지 중 ' +
        '셋에서 나았고 하나는 같았습니다(예: 보수적으로 본 뒤 기간 +70.7% → +104.7%). 계좌가 출렁이는 폭은 같거나 작았습니다. ' +
        '🔴 9/10 변경은 2회차(8/31~9/19) 도중에 적용돼 2회차 성적은 앞부분 +3%와 뒷부분 +4%가 섞여 있습니다. ' +
        '이번 +5%는 3회차 시작(9/21)에 맞췄습니다.',
    },
    {
      heading: '근거',
      body:
        /*
          🔴 2026-09-13 지금 설정 측정(114번)으로 바꿨다
            이전 문구는 감시 50종목·익절 +3% 시절(90번) 숫자였다. 특히 "저점매수는 앞 기간에
            시장보다 7.2%p 뒤졌다" 는 지금 설정(100종목)에서는 사실이 아니다.
        */
        /*
          🔴 2026-09-20 — "지금 설정 그대로" 가 더 이상 맞지 않는다
            3회차(9/21~)부터 익절이 +4% → +5% 로 올랐다(trading.yml aggressive-bounce-pct).
            아래 숫자는 +4% 로 잰 것이므로 "지금 설정" 이라고 부르면 안 된다.
            +5% 로 다시 잰 값은 이 문단 끝에 있다.
        */
        '과거를 앞뒤 두 기간으로 나눠 검증했습니다(2026-09-12, 당시 설정 — ' +
        '하위 40%·익절 +4%·감시 100종목·왕복 체결 손해 0.2% 포함. 🔴 지금은 익절이 +5%입니다). ' +
        '앞 기간 +68.7%(시장 +23.4%), ' +
        '뒤 기간 +171.3%(시장 +57.0%)로 두 기간 모두 시장을 앞섰고, 최대 낙폭은 ' +
        '앞 −10.5%·뒤 −24.6%였습니다. 같은 조건에서 저점매수(하위 20%·+3%)는 ' +
        '앞 +41.8%·뒤 +159.5%, 낙폭 앞 −8.3%·뒤 −21.3%입니다 — 이 계좌가 조금 더 벌고 ' +
        '조금 더 흔들립니다. 차이는 크지 않습니다. ' +
        '🔴 두 기간 모두 시장이 오르던 때라 긴 하락장에서의 모습은 이 검증으로 알 수 없고, ' +
        '"매수 기준가에 그대로 체결된다"고 가정한 결과라 실제 체결이 밀리면 줄어듭니다. ' +
        '🔴 위 숫자는 +4%와 "산 날에는 팔지 못한다" 가정으로 잰 것입니다. 3회차 설정(+5% · 회차 4주)으로 고쳐 다시 재면 ' +
        '앞 +48.5% · 뒤 +104.7%(보수적으로 본 경우) ~ 앞 +52.8% · 뒤 +207.5%(후하게 본 경우)입니다.',
    },
    BAND_REJECTED_SECTION,
    {
      heading: '주의',
      body:
        '더 자주 산다는 것은 더 자주 틀릴 수도 있다는 뜻입니다. 어느 쪽이 나은지는 ' +
        '정해두지 않았습니다 — 그것을 알아보려고 두 계좌를 나란히 굴리는 것입니다.',
    },
  ];
}

/**
 * 저점매수(챌린저) 본문. (2026-09-18, 3회차부터)
 *
 * ✏️ 2026-09-29(I-10) 이 계좌의 지금 값(분위 · 종목당 · 보유 상한 · 순위 끝 · 익절 · 손절)은 설정에서 받는다(ruleWords).
 * 🔴 **다른 계좌(리스크 · 공통 20%)의 값은 사본이다** — 탭 하나는 자기 계좌 설정만 받는다
 */
export function bandChallengerSections(config: BandConfig | null | undefined): Section[] {
  const w = ruleWords(config);
  return [
    {
      heading: '무엇이 다른가',
      body:
        '저점매수(리스크)에서 다섯 가지가 바뀝니다(4회차부터는 보유 상한이 빠져 넷). 첫째, 더 얕게 삽니다 — 최근 6개월 가격의 ' +
        `하위 40% 대신 ${w.percentile}, 즉 더 높은 자리에서도 삽니다. ` +
        '둘째, 나눠 사지 않습니다 — 다른 계좌는 종목당 한도 20%를 세 번에 나눠 사면서 ' +
        `떨어질 때마다 더 담아 평단을 낮추는데, 이 계좌는 종목당 ${w.perStock}를 한 번에 사고 ` +
        // 4회차부터 상한이 null(BandPositionCap) — "제한 없이까지만" 으로 깨지지 않게 문장을 가른다(10/9 병합 전 화면 점검)
        `그걸로 끝입니다. ${config != null && config.maxPositions === null
          ? '셋째, 보유 종목 수에 상한이 없습니다(4회차부터 — 예수금이 다할 때까지). '
          : `셋째, 보유 종목은 ${w.maxPositions}까지만 듭니다(3회차까지). `}` +
        `넷째, 감시 대상 중 왕복성 ${w.maxSwingRank}만 새로 삽니다(3회차부터) — 자리가 적어 ` +
        '순위 낮은 종목이 먼저 걸려 자리를 차지하면 성적이 크게 갈렸습니다. ' +
        `익절은 ${w.bounce}(리스크 계좌는 2026-09-21부터 +5%), 손절은 ${w.stopShort}입니다. ` +
        '익절 +5%도 재봤지만 조건에 따라 결과가 갈려, 이 계좌는 실전 한 회차를 먼저 봅니다.',
    },
    {
      heading: '왜 만들었나',
      body:
        '"리스크 계좌를 더 과감하게" 만들 축을 찾다가, 답이 매도가 아니라 매수 쪽이라는 ' +
        '측정 결과가 나왔기 때문입니다(2026-09-18). 익절폭을 계단으로 나누거나 목표를 ' +
        '높이는 변형은 두 기간 중 한쪽에서만 좋았는데, 매수를 얕고 크게 하는 쪽은 ' +
        '두 기간 모두 나아졌습니다. ' +
        '3회차를 시작할 때 기존 다섯 계좌는 그대로 두고 이 계좌만 바꿔 실전 성적을 회차 단위로 견주기로 했습니다 ' +
        '(3회차 시작에 맞춰 저점매수 손절 · 저점매수(리스크) 익절도 한 칸씩 바뀌었습니다 — 각 계좌 도움말 참조).',
    },
    {
      heading: '근거',
      body:
        '과거를 앞뒤 두 기간으로 나눠 검증했습니다(왕복 체결 손해 0.2% 포함, 감시 100종목). ' +
        '앞 기간 +126.2%(낙폭 −12.0%), 뒤 기간 +299.5%(낙폭 −23.6%)입니다. ' +
        '같은 조건에서 리스크 계좌(하위 40%·나눠 사기)는 앞 +84.3%·뒤 +207.5%였습니다 — ' +
        '두 기간 모두 더 벌면서 계좌가 출렁이는 폭은 비슷합니다. ' +
        '🔴 두 기간 모두 시장이 오르던 때라 긴 하락장에서의 모습은 알 수 없고, ' +
        '"매수 기준가에 그대로 체결된다"고 가정한 결과입니다.',
    },
    {
      heading: '🔴 이 계좌의 한계',
      body:
        '다섯 가지가 한꺼번에 다르기 때문에, 성적이 좋든 나쁘든 ' +
        `깊이·나눠 사기·종목 수(3회차까지)·새로 살 수 있는 왕복성 순위(${w.maxSwingRank})·익절폭(${w.bounce}, 리스크는 +5%) 중 ` +
        '무엇 때문인지 이 계좌 하나로는 가릴 수 없습니다. ' +
        '가르려면 하나씩만 다른 계좌를 더 만들어야 하는데, 계좌가 늘수록 회차당 표본이 줄어듭니다. ' +
        '지금은 "묶음으로 더 나은가"만 봅니다. ' +
        '그리고 나눠 사지 않으므로 한 번 산 뒤에는 평단이 내려가지 않습니다 — ' +
        '다른 계좌가 물타기로 버티는 자리에서 이 계좌는 그대로 손실을 안고 갑니다.',
    },
    BAND_REJECTED_SECTION,
    {
      heading: '주의',
      body:
        '이 계좌는 시험용입니다. 저점매수 · 저점매수(리스크)는 비교 기준으로 그대로 두고, ' +
        '저점매수 규칙을 바꿔 보는 자리를 여기 하나로 모았습니다. ' +
        // "그때는 50종목" 꼬리를 뗐다 - 이 절에는 가리킬 "그때" 가 없다 (2026-09-19 I-59)
        '회차 중간에는 규칙을 바꾸지 않습니다 — 그래야 회차끼리 견줄 수 있습니다.',
    },
  ];
}

export const HELP: Record<PaperTab, HelpContent> = {
  SAFE: {
    title: '🛡️ 안전 계좌',
    oneLiner:
      '조금씩 자주 먹고 빨리 나옵니다. 3회차까지 AI 추천을 사던 세 계좌(안전·분할익절·하이리스크) 중 ' +
      '가장 회전이 빠르고, 4회차부터는 국내 AI 추천을 새로 사는 유일한 계좌입니다.',
    settings: [
      // 🔴 2026-10-08 사용자 결정(I-91) — 4회차부터 국내 추천 중 새로 사는 계좌는 이것 하나다
      { label: '🔴 4회차부터', value: '국내 추천 계좌 중 새로 사는 계좌는 이 계좌 하나입니다 (분할익절 · 하이리스크는 보유만 정리)' },
      { label: '매수 조건', value: '추천 점수 55점 이상' },
      // 🆕 2026-10-10 사용자 결정 — 같은 종목이 새로 추천돼도 들고 있으면 안 산다(전엔 같은 추천만 막아 한 종목에 두 몫을 산 적이 있다 · PaperSameStockGuard)
      { label: '🔴 4회차부터 · 겹친 매수', value: '이미 들고 있는 종목이 새로 추천되면 사지 않습니다 (판 뒤에 다시 추천되면 삽니다)' },
      // 🔴 선은 산 값이 아니라 추천가(추천 당일 종가) 기준이다 - Recommendation 에 절대 가격으로 박힌다 (2026-09-20 정정)
      { label: '익절', value: '추천가 +1.5 ATR' },
      { label: '손절', value: '추천가 −1.0 ATR' },
      // 2026-09-20 사용자 결정 - 파는 쪽만 20:00 까지, 만료는 19:50 (3회차부터, PaperIntradayJob)
      { label: '최대 보유', value: '추천일부터 15영업일 (주말만 빼고 셈 · 넘으면 그날 저녁 19:50에 정리)' },
      { label: '목표 · 손절 감시', value: '09:00~20:00 (정규장 뒤 시간외 포함, 3회차부터)' },
      { label: '원금 · 기간', value: '기간은 회차마다 정합니다 (비교 탭에서 확인). 3회차가 끝난 뒤로는 보유 · 현금을 넘겨 계속 굴립니다(계좌 카드의 큰 숫자는 원금 대비 누적)' },
    ],
    sections: [
      {
        heading: '어떤 종목을 사나',
        body:
          '평일 밤 9시 5분(애프터마켓 마감 뒤 확정 가격으로), 코스피 전 종목을 걸러 "많이 움직이면서 · 거래가 활발하고 · ' +
          '최근에 눌려 있는(단기 과매도)" 종목을 골라 점수를 매깁니다. 55점 이상만 추천이 되고, ' +
          // ✏️ 2026-09-29 I-57 — 장전 매수는 저장 누락으로 3회차에 한 번도 안 돌았다. 사용자 결정으로 4회차부터
          '다음 날 장중에 눌린 자리에 들어온 순간 삽니다(4회차부터는 NXT에서도 거래되는 종목을 장전 08:00~08:50부터). ' +
          '추천이 0건인 날은 사지 않습니다 - ' +
          '기준을 낮춰 억지로 사지 않는 것이 규칙입니다.',
      },
      {
        heading: '언제 파나',
        body:
          '추천가(추천이 나온 날의 종가)에서 +1.5 ATR 오르면 익절, −1.0 ATR 내려가면 손절합니다. ' +
          'ATR은 그 종목이 하루에 보통 몇 원 움직이는지를 뜻하는 금액입니다 — ' +
          '거래량이 아닙니다. 예를 들어 ATR이 6,000원인 종목이 200,000원에 추천됐다면 ' +
          '209,000원(+4.5%)에 익절, 194,000원(−3.0%)에 손절합니다. ' +
          '🔴 선은 산 값이 아니라 추천가에서 긋습니다 — 다음 날 추천가보다 비싸게 샀다면 목표까지는 그만큼 가깝지 않고 ' +
          '손절까지는 그만큼 가깝습니다. ' +
          '%가 아니라 ATR로 긋는 이유는, 하루에 1% 움직이는 종목과 7% 움직이는 종목에 ' +
          '같은 +3%를 걸면 앞엣것은 사흘이 걸리고 뒤엣것은 반나절이면 닿기 때문입니다. ' +
          '목표가 가까워서 자주 도달하는 대신 한 번에 버는 폭이 작습니다. ' +
          '목표 · 손절은 정규장이 끝난 뒤 시간외(20:00까지)에도 봅니다(3회차부터). ' +
          '추천일부터 15영업일이 지나도 어느 선에도 안 닿으면 그날 저녁 19:50에 그냥 팝니다(전에는 15:10). ' +
          '다음 날 사므로 실제로 들고 있는 날은 그보다 하루 짧고, 공휴일도 하루로 셉니다.',
      },
      {
        heading: '근거',
        body:
          '2.1년 과거 데이터로 백테스트했을 때 건당 평균 +0.78%, ' +
          '평균 4.9일 만에 결과가 났습니다. 과거가 미래를 보장하지는 않습니다. ' +
          '🔴 2026-09-19 점검 — 위 숫자는 옛 방식(추천가에 사서 선 가격 그대로 체결)이었습니다. ' +
          '다음 날 실제로 살 수 있는 값 · 체결 손해 · 산 날 청산을 넣어 다시 재면 건당 평균은 두 기간 모두 +0.5% 안팎으로, ' +
          '같은 기간 시장을 그냥 들고 있던 것보다 낮았습니다. 그리고 추천 점수 60~65점대는 두 기간 모두 건당 손실이었습니다.',
      },
    ],
    glossary: [G_ATR, G_CUT, G_PROFIT, G_SCORE, G_BACKTEST],
  },

  MODERATE: {
    title: '⚖️ 분할익절 계좌',
    oneLiner: '절반은 빨리 익절해 안전을 확보하고, 나머지 절반으로 큰 상승을 노립니다.',
    settings: [
      // 🔴 2026-10-08 사용자 결정(I-91) — 4회차부터 국내 추천은 안전 계좌만 산다. 계좌 카드에도 같은 안내가 뜬다(PaperPage · resting)
      // ✏️ 2026-10-10 사용자 결정(141번 10.6) — "잠정 중단": 이 탭 · 비교 탭 · 추천 탭에서 빠지고 회고 · 종합 분석에도 안 들어간다
      { label: '🔴 4회차부터', value: '잠정 중단합니다. 새로 사지 않고, 들고 있는 종목만 목표 · 손절 · 보유 기한으로 정리합니다. 이 탭과 비교 탭 · 추천 탭에서 빠지고 AI 회고도 쓰지 않습니다. 3회차까지의 성적은 📚 회차 이력에 그대로 남습니다 (국내 추천은 안전 계좌만 남기고, 빈 자리는 국내 스윙 계좌가 씁니다)' },
      { label: '매수 조건', value: '추천 점수 55점 이상 (안전과 같음)' },
      { label: '1차 익절', value: '추천가 +1.5 ATR 에서 절반' },
      { label: '1차 익절 후', value: '손절선을 매수가(본전)로 올림' },
      { label: '최종 익절', value: '추천가 +3.0 ATR (남은 절반)' },
      { label: '손절', value: '추천가 −1.5 ATR' },
      { label: '최대 보유', value: '추천일부터 15영업일 (주말만 빼고 셈 · 넘으면 그날 저녁 19:50에 정리)' },
      { label: '목표 · 손절 감시', value: '09:00~20:00 (정규장 뒤 시간외 포함, 3회차부터)' },
    ],
    sections: [
      {
        heading: '어떤 종목을 사나',
        body:
          '안전 계좌와 똑같습니다. 세 계좌는 같은 종목을 사고 "언제 파는가"만 다릅니다 - ' +
          '그래야 매도 방식의 차이만 순수하게 비교됩니다.',
      },
      /*
        🔴 2026-09-12 에 이 자리에 "왜 이 계좌를 먼저 참고하라고 하나" 절이 있었다. 뒤집혔다 (2026-09-13)

          사용자 질문("대출을 갚고 시드를 키우려는 사람이라면 어느 프로필을 참고하나")에
          분할익절이라고 답하고 근거로 "워크포워드 검증에서 추천 3계좌 중 유일하게 플러스
          (+21.3%)" 를 적었다. 이튿날 다시 보니 두 가지를 틀렸다.

            ① 필터를 켜기 전(101번) 숫자였다. 지금 설정(105번, 섹터 모멘텀 켬)에서는
               셋 다 검증에서 플러스이고 분할익절은 시장에 진다(+34.5% vs +43.9%)
            ② 추천 3계좌끼리만 비교했다. 저점매수 두 계좌와 같은 잣대(시장 대비 초과·
               최악 낙폭)로 놓으면 둘 다 분할익절보다 수익이 크고 낙폭이 절반이다(114번)

          조용히 지우지 않고 절을 바꿔 남긴다 - 몇 달 뒤 "분할익절이 1순위 아니었나" 를
          다시 묻게 되기 때문이다. (docs/impl/114 9절)
      */
      {
        heading: '🔴 이 계좌를 따라가도 되나 (2026-09-13 정정)',
        body:
          '2026-09-12 에 이 자리에 "대출을 갚고 시드를 키우려면 이 계좌를 먼저 참고하라"고 ' +
          '적었는데, 틀린 근거였습니다. 바로잡습니다. ' +
          '그때 인용한 "검증에서 추천 3계좌 중 유일하게 플러스(+21.3%)"는 업종 필터를 켜기 전의 ' +
          '숫자였습니다. 지금 설정으로 과거를 앞뒤로 갈라 확인하면 뒤 기간에 이 계좌는 +34.5%로, ' +
          '같은 기간 시장(+43.9%)보다 9.4%p 낮았고 최대 낙폭은 −41.4%였습니다. ' +
          '추천 3계좌끼리만 견줬던 것도 문제였습니다 — 다섯 계좌를 같은 잣대로 놓으면 저점매수 ' +
          '두 계좌가 앞뒤 두 기간 모두 시장을 크게 이기면서 낙폭은 이 계좌의 절반 수준입니다 ' +
          '(비교 탭 도움말의 "다섯 계좌를 같은 잣대로 · 챌린저 제외" 참조). ' +
          '분할 출구(절반 익절 후 손절선을 본전으로 올림)는 여전히 이 계좌의 장점이지만, ' +
          '그것만으로 따라갈 1순위라고 말할 근거는 없습니다.',
      },
      {
        heading: '언제 파나 (분할 매도)',
        body:
          '+1.5 ATR에서 절반을 먼저 익절합니다. 그 순간 손절선을 매수가(본전)로 ' +
          // ✏️ 9/28 I-78 2차 — 본전선은 매수가 그대로라 수수료 · 세금을 빼면 소폭 손실이고 승률에서도 패로 센다
          '올립니다 - 이제 최악의 결말이 큰 손실이 아니라 본전 청산이 됩니다(수수료 · 세금만큼은 조금 잃고, 승률에서는 패로 셉니다). ' +
          '남은 절반은 +3.0 ATR까지 기다립니다. ' +
          '(ATR은 그 종목이 하루에 보통 몇 원 움직이는지를 뜻하는 금액입니다. ' +
          'ATR이 6,000원이면 +1.5 ATR은 추천가보다 9,000원 위입니다. 선은 산 값이 아니라 추천가에서 긋습니다.) ' +
          '목표 · 손절은 시간외(20:00까지)에도 보고, 추천일부터 15영업일이 지나면 그날 19:50에 정리합니다(3회차부터).',
      },
      {
        heading: '근거',
        body:
          '백테스트에서 건당 평균 +1.87%로 단일 매도(+1.58%)보다 나았습니다. ' +
          '절반 익절에 성공한 비율이 54%였습니다. 사용자의 실제 매매 습관 ' +
          '(부분 익절 후 나머지를 달리게 두기)을 기계 규칙으로 옮긴 것입니다. ' +
          '🔴 2026-09-19 점검 — 다음 날 실제로 살 수 있는 값 · 체결 손해 · 산 날 청산을 넣어 다시 재면 ' +
          '건당 평균은 이 계좌가 +1.0~1.1%, 한 번에 파는 하이리스크가 +1.5% 안팎으로 순서가 뒤집혔습니다(이긴 비율은 이 계좌가 더 높습니다). ' +
          '어느 쪽도 같은 기간 시장을 그냥 들고 있던 것보다 뚜렷이 낫지 않았습니다.',
      },
    ],
    glossary: [G_ATR, G_CUT, G_PROFIT, G_SCORE, G_BACKTEST],
  },

  AGGRESSIVE: {
    title: '⚡ 하이리스크 계좌',
    oneLiner: '큰 상승(+3 ATR)만 노립니다. 자주 못 먹는 대신 먹을 때 큽니다.',
    settings: [
      // 🔴 2026-10-08 사용자 결정(I-91) — 분할익절과 같다
      // ✏️ 2026-10-10 사용자 결정(141번 10.6) — "잠정 중단": 이 탭 · 비교 탭 · 추천 탭에서 빠지고 회고 · 종합 분석에도 안 들어간다
      { label: '🔴 4회차부터', value: '잠정 중단합니다. 새로 사지 않고, 들고 있는 종목만 목표 · 손절 · 보유 기한으로 정리합니다. 이 탭과 비교 탭 · 추천 탭에서 빠지고 AI 회고도 쓰지 않습니다. 3회차까지의 성적은 📚 회차 이력에 그대로 남습니다 (국내 추천은 안전 계좌만 남기고, 빈 자리는 국내 스윙 계좌가 씁니다)' },
      { label: '매수 조건', value: '추천 점수 55점 이상 (안전과 같음)' },
      { label: '익절', value: '추천가 +3.0 ATR' },
      { label: '손절', value: '추천가 −1.5 ATR' },
      { label: '손익비', value: '2 : 1 (안전 계좌는 1.5 : 1)' },
      { label: '최대 보유', value: '추천일부터 15영업일 (주말만 빼고 셈 · 넘으면 그날 저녁 19:50에 정리)' },
      { label: '목표 · 손절 감시', value: '09:00~20:00 (정규장 뒤 시간외 포함, 3회차부터)' },
    ],
    sections: [
      {
        heading: '어떤 종목을 사나',
        body:
          '안전·분할익절 계좌와 똑같습니다. 처음에는 "더 위험한 종목"을 따로 고르는 ' +
          '설계였지만, 백테스트에서 크게 밀린 고변동 종목은 어떤 매도 규칙으로도 ' +
          '손해였습니다(떨어지는 칼). 그래서 종목은 같게 두고 파는 방식만 다르게 갑니다.',
      },
      {
        heading: '언제 파나',
        body:
          '추천가 +3.0 ATR 도달 시 익절, −1.5 ATR에서 손절합니다. ' +
          '(ATR은 그 종목이 하루에 보통 몇 원 움직이는지를 뜻하는 금액입니다. ' +
          'ATR이 6,000원이면 +3.0 ATR은 추천가보다 18,000원 위입니다. 선은 산 값이 아니라 추천가에서 긋습니다.) ' +
          '목표 · 손절은 시간외(20:00까지)에도 보고, 추천일부터 15영업일이 지나면 그날 19:50에 정리합니다(3회차부터). ' +
          '목표가 멀어 승률은 31%로 낮지만, 맞을 때 버는 폭이 커서 ' +
          '건당 평균으로는 AI 추천을 사는 세 계좌 중 가장 높았습니다(+2.32%). ' +
          '저점매수는 종목을 고르는 방식부터 달라 이 비교에 넣지 않습니다. ' +
          '🔴 2026-09-19 점검에서 다음 날 실제로 살 수 있는 값 · 체결 손해를 넣어 다시 재도 세 계좌 중 건당 평균이 가장 높았습니다(+1.5% 안팎). ' +
          '다만 목표가 멀어(ATR이 큰 종목은 추천가보다 20% 넘게 위) 15영업일 안에 닿지 않는 일이 잦습니다 — 2회차에는 한 번도 닿지 않았습니다.',
      },
      {
        heading: '주의',
        body:
          '보유가 길어지고(평균 7.4일) 결과가 안 난 채 만료되는 비율(32%)이 ' +
          '높습니다. 계좌 잔고가 출렁이는 것을 견뎌야 하는 성격입니다.',
      },
    ],
    glossary: [G_ATR, G_CUT, G_PROFIT, G_SCORE, G_BACKTEST],
  },

  BAND: {
    title: '📉 저점매수 계좌',
    oneLiner:
      '늘 오르내리는 종목을 정해두고, 싸질 때 사서 반등하면 팝니다. 사용자의 실제 매매 방식입니다.',
    // 🔴 저점매수는 서버 응답으로 채운다. bandSettings() 를 보라
    settings: [],
    // 🔴 본문도 서버 설정을 받는다 (감시 대상 수). bandSections() 를 보라
    sections: bandSections(undefined),
    sectionsFor: bandSections,
    glossary: [
      G_SWING,
      G_WINDOW,
      G_PERCENTILE,
      G_LADDER,
      G_BAND_EXIT,
      G_PROFIT,
      G_MOMENTUM,
      G_ATR,
      G_CUT,
      G_BACKTEST,
      G_DRAWDOWN,
    ],
  },

  BAND_AGGRESSIVE: {
    title: '🔥 저점매수(리스크) 계좌',
    oneLiner:
      '저점매수와 같은 규칙인데 조금만 눌려도 삽니다. 자주 사는 대신 더 출렁입니다.',
    // 🔴 저점매수와 마찬가지로 서버 응답으로 채운다. bandSettings() 를 보라
    settings: [],
    // 🔴 본문도 서버 설정을 받는다 (감시 대상 수). bandAggressiveSections() 를 보라
    sections: bandAggressiveSections(undefined),
    sectionsFor: bandAggressiveSections,
    glossary: [
      G_PERCENTILE,
      G_WINDOW,
      G_SWING,
      G_LADDER,
      G_BAND_EXIT,
      G_PROFIT,
      G_MOMENTUM,
      G_ATR,
      G_CUT,
      G_BACKTEST,
      G_DRAWDOWN,
    ],
  },

  BAND_CHALLENGER: {
    title: '🧪 저점매수(챌린저) 계좌',
    oneLiner:
      '리스크 계좌보다 더 얕게, 나눠 사지 않고 한 번에 삽니다. 규칙을 시험하는 자리입니다.',
    // 🔴 저점매수 계열과 마찬가지로 서버 응답으로 채운다. bandSettings() 를 보라
    settings: [],
    // 🔴 본문도 서버 설정을 받는다 (감시 대상 수). bandChallengerSections() 를 보라
    sections: bandChallengerSections(undefined),
    sectionsFor: bandChallengerSections,
    glossary: [
      G_PERCENTILE,
      G_WINDOW,
      G_SWING,
      G_BAND_EXIT,
      G_BACKTEST,
      G_DRAWDOWN,
    ],
  },

  // 🔁 국내 스윙 — 설정 · 본문은 서버 응답으로 채운다(HelpBody · swingHelp.ts). 여기는 설정을 모를 때의 껍데기다
  SWING_A: {
    title: '🔁 스윙(6개월) 계좌',
    oneLiner: '고정 다섯 섹터의 종목이 최근 6개월 가격 폭의 아래쪽에 오면 나눠 사고, 오를 때마다 나눠 팝니다.',
    settings: [],
    sections: swingSections(undefined),
    glossary: SWING_GLOSSARY,
  },
  SWING_C: {
    title: '🔁 스윙(3개월) 계좌',
    oneLiner: '스윙(6개월)과 같은 규칙인데 최근 3개월 가격 폭으로 진입선을 잡고 세 단을 같은 금액으로 나눕니다.',
    settings: [],
    sections: swingSections(undefined),
    glossary: SWING_GLOSSARY,
  },
  // 🇺🇸 미국 스윙(2026-10-10) — 미국 화면의 "?" 에서만. 설정 · 본문은 서버 응답(config.currency USD)으로 미국판을 고른다
  SWING_US_A: {
    title: '🔁 미국 스윙(6개월) 계좌',
    oneLiner: '국내 스윙(6개월) 규칙을 값 그대로 미국 대형주에 옮긴 시험 계좌입니다. 원화로 사고팔고 가격 · 규칙은 달러로 셉니다.',
    settings: [],
    sections: swingSections(undefined),
    glossary: SWING_GLOSSARY,
  },

  // 🇺🇸 미국 추천 R3(2026-10-11 E 화면) — 미국 화면의 "?" 에서만. 규칙 원본 = backend UsRecoRules · trading.yml screener.us-safe-r3 · 145번 3.7 등록
  US_SAFE_R3: {
    title: '🇺🇸 미국 추천 계좌',
    oneLiner:
      '덜 출렁이는 미국 대형주가 상승 추세 속에서 살짝 눌렸을 때 사서, 국내 안전 계좌처럼 짧게 먹고 나옵니다. 원화로 사고팔고 가격 · 규칙은 달러로 셉니다.',
    settings: [
      { label: '시작', value: '4회차부터 고릅니다' },
      { label: '고르는 범위', value: '미국 대형주 83종목 중 최근 20일 평균 거래대금이 하한 이상인 종목 (GOOG는 GOOGL과 같은 회사라 뺍니다)' },
      { label: '고르는 조건', value: '그날 하루 진폭(ATR%)이 가운데 이하 · 20일선 ≥ 60일선 · 종가 ≥ 120일선 · 20일 이격도 95~99.5 · RSI 35~50' },
      { label: '추천 수', value: '조건을 통과한 종목 중 거래대금 큰 순으로 최대 10개 (최근 5거래일 안에 추천된 종목은 뺍니다)' },
      { label: '살 때', value: '장중 그 순간의 20일 이격도가 다시 95~99.5 안일 때만 삽니다 · 이미 들고 있는 종목은 사지 않습니다' },
      { label: '익절', value: '추천가 +1.5 ATR' },
      { label: '손절', value: '추천가 −1.0 ATR' },
      { label: '최대 보유', value: '미국 거래일 15일 (넘으면 그날 정규장 마감 10분 전부터 정리)' },
      { label: '매매 시각', value: '한국 시각 밤 미국 정규장 (뉴욕 시각 09:35 · 12:00 · 15:40 점검 + 장중 매분)' },
      { label: '수수료', value: '사고팔 때 0.1% · 미국은 거래세가 없습니다' },
    ],
    sections: [
      {
        heading: '어떤 종목을 사나',
        body:
          '매일 오전 10:45, 그날 밤 미국 장이 열리는 날만 직전 미국 거래일 종가로 고릅니다. ' +
          '먼저 미국 대형주 83종목 중 거래가 충분한 종목만 남기고, 그중 그날 하루 진폭(ATR%)이 가운데 이하인 — 덜 출렁이는 — 종목만 봅니다. ' +
          '그다음 상승 추세(20일선이 60일선 위 · 종가가 120일선 위) 속에서 얕게 눌린(20일 이격도 95~99.5 · RSI 35~50) 종목을 거래대금 큰 순으로 최대 10개 고릅니다. ' +
          '조건을 통과한 종목이 0개인 날은 사지 않습니다 — 기준을 낮춰 억지로 고르지 않는 것이 규칙입니다.',
      },
      {
        heading: '언제 사고 파나',
        body:
          '그날 밤 미국 정규장 동안 매분 지켜보다가, 장중 20일 이격도가 다시 95~99.5 안에 들어온 순간 삽니다(국내 안전 계좌의 92~98과 다릅니다). ' +
          '추천가(추천을 고른 날의 종가)에서 +1.5 ATR 오르면 익절, −1.0 ATR 내려가면 손절합니다. ATR은 그 종목이 하루에 보통 몇 달러 움직이는지입니다. ' +
          '미국 거래일로 15일이 지나도 어느 선에도 안 닿으면 그날 정규장 마감 10분 전부터 정리합니다(조기 폐장일은 그날 마감 10분 전).',
      },
      {
        heading: '돈은 어떻게 세나',
        body:
          '계좌는 원화 2,000만 원으로 시작합니다. 살 때 그 순간 환율로 원화를 달러로 바꾸고, 팔 때 다시 원화로 바꿉니다. ' +
          '위 계좌 카드는 원화이고(보유 종목은 지금 환율로 바꿔 더합니다), 아래 표의 가격 · 금액 · 손익은 달러입니다. ' +
          '그래서 같은 거래라도 달러로는 이익인데 환율이 내려 원화로는 손실일 수 있습니다. ' +
          '하루 평가는 미국 장이 끝난 다음 날 오전 11:00에 그날 종가와 그 시각 환율로 남깁니다(거래마다 쓰던 AI 회고는 2026년 10월 18일부터 멈췄습니다).',
      },
      {
        heading: '알아 둘 것',
        body:
          '이 규칙은 결과를 보기 전에 정해 둔 것이고(등록 규칙), 미국 과거 백테스트 수익은 판단 근거로 쓰지 않습니다 — 앞으로 쌓이는 기록으로 봅니다. ' +
          '실제 주문은 나가지 않습니다. 매수 · 매도마다 알림을 보내지 않고, 아침 06:50에 밤사이 매매를 한 통으로 묶어 보냅니다.',
      },
    ],
    glossary: [G_ATR, G_CUT, G_PROFIT, G_BACKTEST],
  },

  BAND_US_P20: {
    title: '🇺🇸 미국 저점매수 계좌',
    oneLiner: '국내 저점매수와 같은 방식을 미국 종목에 옮긴 계좌입니다. 원화로 사고팔고 환율 몫을 따로 셉니다.',
    // 🔴 서버 응답으로 채운다 — usBandSettings() 를 보라
    settings: [],
    sections: usBandSections(undefined),
    sectionsFor: usBandSections,
    glossary: [G_SWING, G_WINDOW, G_PERCENTILE, G_LADDER, G_BAND_EXIT, G_PROFIT, G_ATR, G_CUT, G_BACKTEST],
  },

  COMPARISON: {
    // ✏️ 2026-10-10 "여섯 계좌 비교" → 계좌 수를 빼고 회차마다 구성을 적는다(4회차부터 분할익절 · 하이리스크 쉼 · 스윙 둘 합류)
    title: '계좌 비교',
    oneLiner: '서로 다른 매매 방식을 나란히 굴려 무엇이 나은지 실험합니다.',
    settings: [
      {
        label: '비교 대상(3회차까지)',
        value: '안전 · 분할익절 · 하이리스크 · 저점매수 · 저점매수(리스크) · 저점매수(챌린저, 3회차부터)',
      },
      // 🔴 2026-10-10 사용자 결정(141번 10.6 · 12.6) — 분할익절 · 하이리스크 잠정 중단 · 스윙 둘 합류. 서버가 지금 회차 판정으로 거른다
      {
        label: '🔴 4회차부터',
        value:
          '안전 · 저점매수 · 저점매수(리스크) · 저점매수(챌린저) · 스윙(6개월) · 스윙(3개월). 분할익절 · 하이리스크는 잠정 중단해 빠지고, 3회차까지의 성적은 📚 회차 이력에 그대로 있습니다',
      },
      { label: '순위 기준', value: '회차 시작 이후 수익률 (%)' },
      { label: '곡선의 점선', value: '손익 0% 선 · 저점매수(리스크) · 저점매수(챌린저, 일점쇄선) · 스윙(3개월, 짧은 점선) · 시장 기준선' },
      { label: '곡선의 세로 점선', value: '규칙이 바뀐 날 (예: 09-10 저점매수(리스크) 익절 +4%)' },
      { label: '시장 기준선', value: '코스피 종목을 똑같은 금액씩 사서 들고 있었을 때' },
      // 🔴 2026-09-25 사용자 결정(U-31) - 3회차 종료부터 회차 = 성적표 마감일. 팔지 않고 넘긴다
      // ✏️ 2026-10-19 — 계좌 카드 큰 숫자가 원금 대비 누적으로 바뀌었다(사용자 피드백) · 회차 성적은 그대로 회차 시작 대비
      { label: '회차', value: '기간을 정해 시작합니다. 이번 회차 수익률은 회차 시작 금액 대비(3회차까지는 원금, 그 뒤는 시작일 평가액) · 계좌 카드의 큰 숫자(누적)는 처음 원금 대비' },
      {
        label: '회차가 끝나면',
        value: '20:40 성적표(그날 종가 평가액) → 21:30 종합 분석. 보유는 팔지 않고 넘깁니다 (2회차까지는 전량 청산)',
      },
      { label: '회차 사이', value: '새 회차가 시작될 때까지 새로 사지 않고, 들고 있는 종목의 매도 규칙(목표 · 손절 · 만료 · 1차 익절 · 본전)만 돕니다' },
    ],
    sections: [
      {
        heading: '어떤 계좌들인가',
        // 🔴 이 절의 숫자(+3 대 +5% · 1.5 대 2 ATR · 50% · 10% · 8종목 · 상위 30)는 세 저점매수 계좌 설정의 **사본**이다 (2026-09-29 I-10)
        //   비교 탭은 계좌 하나의 설정만 받는 구조가 아니라 연동하지 못했다. trading.yml band 블록을 고치면 여기도 본다
        body:
          '추천 계열과 저점매수 계열로 나뉩니다(4회차부터 스윙이 세 번째 계열). 추천 계열은 3회차까지 안전·분할익절·하이리스크가 같은 종목을 사되 ' +
          '"언제 파는가"만 달랐습니다 - 매도 전략 실험이었고, 4회차부터는 안전 하나만 새로 삽니다. ' +
          '저점매수와 저점매수(리스크)는 감시 종목 · 나눠 사기가 같고 "얼마나 눌렸을 때 사는가"와 ' +
          '익절 폭(9/21부터 +3% 대 +5%) · 손절 폭(9/21부터 1.5 대 2 ATR)이 다릅니다 - 매수 깊이 실험입니다. 저점매수(챌린저)는 3회차부터 붙은 도전자로, ' +
          '리스크 계좌에서 "더 얕게(50%) · 나눠 사기 대신 한 번에 종목당 10% · 최대 8종목(3회차까지 · 4회차부터 상한 없음) · 왕복성 상위 30종목만" 을 시험합니다. ' +
          '그리고 두 계열끼리는 종목 고르는 방식부터 ' +
          '다릅니다 - AI가 발굴한 종목을 스윙하는 것과, 늘 보던 종목들(저점매수 감시 대상)을 ' +
          '반복 매매하는 것 중 무엇이 나은지를 봅니다. ' +
          // 🔁 2026-10-10 · 141번 12.6 — 숫자(6개월 · 3개월 · 다섯 섹터)는 계좌 이름 · 구성 자체라 박아 둔다. 규칙 값은 스윙 탭 "?" 가 서버에서 받는다
          '4회차부터는 세 번째 계열인 스윙 두 계좌가 붙습니다. 정해 둔 다섯 섹터의 종목을 범위 아래쪽에서 나눠 사서 ' +
          '오를 때마다 나눠 팔고, 오래 들면 정리합니다. 두 계좌는 진입선을 잡는 기간(최근 6개월 · 3개월)과 단별 금액 비율만 다릅니다. ' +
          '🔴 스윙은 한 종목을 길게는 두 달 가까이 들어서, 4주 회차 하나로는 청산이 적고 수익률에 아직 안 판 평가 손익이 많이 섞입니다.',
      },
      /*
        🔴 실험의 한계를 화면에 적는다 (2026-08-31 사용자 요청)

          비교 화면은 순위를 보여주기 때문에 "1등이 정답" 으로 읽히기 쉽다.
          실제로 2026-08-30 측정에서 앞 구간 1등이 뒤 구간에서 시장에 크게 진 일이
          있었다(+66.5% → -8.5%). 그때 배운 것을 여기 적어 둔다.
      */
      {
        heading: '🔴 이 비교로 알 수 있는 것과 없는 것',
        body:
          '알 수 있는 것은 "같은 기간·같은 시장에서 어느 방식이 더 나았나"입니다. ' +
          '계열 안에서 다른 점을 몇 가지로 좁혀 뒀기 때문에 차이의 원인도 그 안에서 찾을 수 있습니다. ' +
          '다만 두 가지 이상이 함께 다르면(리스크는 저점매수와 깊이 · 익절 · 손절, 챌린저는 리스크와 깊이 · 한 번에 사기 · 보유 상한(3회차까지) · 순위 · 익절) ' +
          '그중 무엇 때문인지는 이 비교만으로 가를 수 없습니다 — 그건 과거 데이터 측정으로 따로 봅니다. ' +
          '알 수 없는 것은 세 가지입니다. ' +
          '첫째, 모든 계좌가 같은 시장을 지나므로 시장이 오르면 다 오릅니다 — ' +
          '수익률의 크기가 아니라 서로의 차이만 의미가 있습니다. ' +
          '둘째, 한 회차는 한 국면입니다. 과거 검증에서 앞 1년의 1등이 뒤 1년에는 ' +
          '손실로 뒤집힌 적이 있습니다. ' +
          '셋째, 매매가 몇십 건일 때의 순위는 며칠 만에 바뀝니다.',
      },
      {
        heading: '🔴 회색 점선 — 시장 기준선',
        body:
          '곡선 사이에 얇은 회색 점선이 하나 있습니다. 계좌가 아니라 기준선입니다 — ' +
          '같은 기간에 코스피 종목을 똑같은 금액씩 나눠 사서 그냥 들고 있었으면 ' +
          '얼마였는지입니다. 이 선보다 아래에 있는 계좌는, 아무것도 고르지 않고 ' +
          '그냥 다 샀을 때보다 못한 것입니다. ' +
          '코스피 지수와는 다릅니다 — 지수는 삼성전자처럼 큰 회사가 더 크게 반영되는데, ' +
          '우리 계좌들은 종목마다 비슷한 금액을 넣기 때문에 기준선도 그렇게 맞췄습니다. ' +
          '🔴 이 선이 없으면 순위가 거짓말을 합니다. "1등 +20%"를 보고 좋아할 수 있는데, ' +
          '그 기간 시장이 +50%였다면 모두 진 것입니다. ' +
          '기준선은 2026-08-31부터 쌓이며 그 전 구간에는 없습니다.',
      },
      /*
        🔴 회차 도중 규칙이 바뀐 날을 곡선 위에 세로로 긋는다 (2026-09-12)
          "회차란" 절이 "회차 중에는 규칙을 안 바꾼다" 고 말하는데, 실제로는 2회차 도중에
          두 번 바뀌었다. 선을 안 그으면 그 앞뒤 성적이 한 규칙의 것처럼 읽힌다.
          표시되는 날짜·내용이 바뀌면 여기도 같은 커밋에서 고친다.
      */
      {
        heading: '세로 점선 — 규칙이 바뀐 날',
        body:
          '회차 도중 규칙이 바뀌면 그 앞뒤 성적을 한 규칙의 것으로 읽을 수 없어, 바뀐 날에 ' +
          '세로 점선을 긋습니다. 선 왼쪽과 오른쪽은 다른 규칙의 성적입니다. ' +
          // ✏️ 9/28 I-78 2차 — 곡선은 이번 회차 시작일부터라, 곡선 안에 든 날짜의 점선만 보인다(3회차 화면은 09-21 하나)
          '곡선은 이번 회차 시작일부터 그려서, 그 안에 든 날짜의 점선만 보입니다. ' +
          '지금까지 기록된 것: 2026-09-10 저점매수(리스크) 익절 +3%→+4%, ' +
          '2026-09-12 저점매수 감시 대상 50→100종목(다음 재선정부터 적용), ' +
          '2026-09-21 3회차 규칙(저점매수 매매 시간 09:00~15:30 → 08:00~20:00 · 챌린저는 왕복성 상위 30종목만 새로 삼 · ' +
          '저점매수 세 계좌는 이미 손절선 아래 가격이면 사지 않음 · 저점매수 손절 2 → 1.5 ATR · ' +
          '저점매수(리스크) 익절 +4% → +5% · 추천 계좌 NXT 장전 매수는 오류로 3회차에는 돌지 않음(4회차부터) · ' +
          '추천 계좌 목표 · 손절 감시 15:30 → 20:00, 만료 정리 15:10 → 19:50).',
      },
      {
        heading: '그래서 어떻게 봐야 하나',
        body:
          '한 회차의 1등을 정답으로 삼지 말고, 짧은 회차를 여러 번 돌려 ' +
          '"여러 국면에서 꾸준히 위에 있는 계좌"를 찾는 편이 낫습니다. ' +
          '그리고 수익률보다 최대 낙폭(계좌가 가장 크게 출렁인 폭)을 먼저 보세요 — ' +
          '과거 검증에서 기간이 바뀌어도 이어지는 성질은 수익률이 아니라 낙폭이었습니다. ' +
          '수익률 순위는 기간이 바뀌면 거의 무작위에 가까웠습니다. ' +
          '순위와 함께 회색 기준선을 반드시 확인하세요.',
      },
      /*
        🔴 다섯 계좌를 같은 잣대로 (2026-09-13 사용자 질문에서)
          "실사용자라면 어느 프로필을 참고하나" 에 한 번 틀리게 답했다 - 계열 안에서만 비교했다.
          두 계열은 하네스가 달라 기간·분할점이 조금씩 다르므로 수익률을 그대로 견주면 안 되고,
          각자 같은 실행의 시장 대비 초과분과 최악 낙폭으로 놓아야 한다. (docs/impl/114 9절)
          숫자 출처: 추천 계열 105번, 저점매수 계열 114번. 설정이 바뀌면 여기도 고친다.
      */
      {
        heading: '🔴 다섯 계좌를 같은 잣대로 (2026-09-13 · 챌린저 제외)',
        body:
          '두 계열은 검증한 기간이 조금씩 달라서 수익률을 그대로 견주면 안 됩니다. 그래서 ' +
          '각자 같은 검증에서 "시장보다 얼마나 나았나"와 "가장 크게 빠진 폭"으로 놓았습니다 ' +
          '(과거를 앞뒤로 갈라 확인한 뒤 기간 기준). ' +
          '저점매수(리스크) 시장보다 +114.3%p · 낙폭 −24.6%, 저점매수 +102.5%p · −21.3%, ' +
          '안전 +10.8%p · −35.7%, 분할익절 −9.4%p · −41.4%, 하이리스크 −43.5%p · −50.2%입니다. ' +
          '앞 기간까지 두 기간 모두 시장을 이긴 것은 저점매수 두 계좌와 안전입니다. ' +
          '비용 전제는 저점매수 쪽이 더 엄격합니다 — 추천 계열 검증은 수수료·세금만 넣었고, ' +
          '저점매수는 사고팔 때마다 0.2%의 체결 손해까지 넣었습니다. ' +
          '🔴 3회차부터 붙은 저점매수(챌린저)는 이 표에 없습니다. ' +
          '이 표는 "같은 잣대로 놓는 것"이 목적인데, 챌린저 검증은 체결을 후하게 보는 다른 가정으로 ' +
          '쟀기 때문입니다 — 같은 리스크 계좌가 이 표에서는 뒤 기간 +171.3%, 챌린저 비교에서는 ' +
          '+207.5%로 나옵니다. 수치를 나란히 놓으면 챌린저가 실제보다 좋아 보입니다. ' +
          '챌린저 숫자는 그쪽 계좌 도움말에서 같은 가정으로 잰 리스크 계좌와 함께 보세요. ' +
          '3회차(2026-09-21~)부터 실전 성적이 쌓이면 그때는 같은 잣대로 놓을 수 있습니다. ' +
          '스윙 두 계좌(4회차부터)도 이 표에 없습니다 — 다른 측정(141번)으로 쟀고, 두 계좌의 차이는 ' +
          '몇 회차로는 가릴 수 없다는 결과라 승패를 판정하지 않고 나란히 기록합니다. ' +
          '🔴 한계가 셋 있습니다. 두 기간 모두 시장이 오르던 때라 긴 하락장은 검증되지 않았습니다. ' +
          '저점매수는 "매수 기준가에 그대로 체결된다"고 가정했고 매매가 잦아 실제 체결에 더 민감합니다. ' +
          '그리고 과거 검증이 미래를 보장하지 않습니다.',
      },
      {
        heading: '순위는 어떻게 매기나',
        body:
          '계좌마다 회차 시작 금액이 다를 수 있어 금액이 아니라 수익률(%)로 줄 세웁니다. ' +
          '3회차가 끝난 뒤로는 보유를 넘기므로 계좌마다 크기가 더 벌어집니다. ' +
          // 2026-10-19 — 계좌 카드의 큰 숫자가 원금 대비 누적으로 바뀌었다. 비교는 회차 수익률로 줄 세우고 누적은 작게만 적는다
          '각 줄의 작은 "누적" 은 처음 원금 대비 수익률입니다 — 계좌마다 시작한 회차가 달라(스윙은 4회차부터) ' +
          '기간이 다르므로 순위에는 쓰지 않습니다. ' +
          '🔴 다만 순위 옆에 청산 건수를 함께 적습니다 - 매매가 몇 건뿐일 때의 순위는 ' +
          '며칠 만에 뒤집힙니다. 시스템은 순위를 보여줄 뿐 "어느 계좌가 이겼다"고 ' +
          '선언하지 않습니다.',
      },
      {
        heading: '회차란',
        body:
          '실험 한 판입니다. 기간을 정해 시작하고, 그동안은 중간에 규칙을 바꾸지 ' +
          '않습니다 - 규칙이 바뀌면 그 성적이 어느 규칙의 것인지 알 수 없기 때문입니다. ' +
          '🔴 3회차가 끝난 뒤로(2026-10-16~) 회차는 "성적표를 적는 날" 입니다. 마지막 날 20:40에 ' +
          '그날 종가로 평가한 금액을 적고, 보유는 팔지 않고 다음 회차로 넘깁니다. 현금 · 시드도 ' +
          '되돌리지 않고 그 평가액에서 다음 회차가 출발합니다 — 실제 계좌와 같은 모양이어야 ' +
          '모의 성적을 실전에 옮길 수 있기 때문입니다. 넘어간 종목은 산 때의 규칙으로 끝내고, ' +
          '회차 사이에는 새로 사지 않고, 들고 있는 종목의 매도 규칙(목표 · 손절 · 만료 · 1차 익절 · 본전)만 돕니다. 그리고 그동안 쌓인 ' +
          '계좌별 성적을 묶어 종합 분석을 냅니다(3회차까지는 회고도). 4회차부터는 코드가 센 거래 사실을 함께 넣습니다 — ' +
          '손익분기 승률 · 손절이 몰린 날 · 장 끝 노출(장 끝에 보유 종목이 총자산에서 차지한 몫), 그리고 모든 청산을 보유 중 시장(시장 평균 종목)이 −3% 이하였나 · ' +
          '진입 전 20거래일이 −10% 이하였나로 나눈 손절 비율입니다. 이 나눔은 손절의 원인이 아니라 그때의 환경이라, ' +
          'AI 는 이 숫자를 옮겨 쓰고 “~때문에” 같은 원인 판정은 하지 않습니다. 2회차까지는 마지막 날 보유를 모두 ' +
          '정리했고, 그 거래는 매매 판단이 아니라 기간 때문에 판 것이라 회고하지 않았습니다.',
      },
      {
        heading: '기간은 얼마가 좋은가',
        body:
          '3~4주를 권합니다. 추천 · 저점매수 계좌는 평균 며칠 만에 사고파는 단기매매라, ' +
          '몇 달을 한 회차로 묶으면 그동안 규칙을 못 바꾸면서 국면 하나만 보게 됩니다. ' +
          '짧은 회차를 여러 번 돌리는 편이 서로 다른 국면의 데이터를 빨리 쌓습니다. ' +
          '다만 너무 짧으면 매매 건수가 모자랍니다 — 영업일 20일(4주) 기준으로 ' +
          '저점매수 계열은 30~40건쯤 쌓이지만 추천 계좌는 10~20건입니다. ' +
          '스윙은 한 종목을 길게는 두 달 가까이 들어서 한 회차 청산이 몇 건뿐일 수 있습니다. ' +
          '청산 10건 미만이면 화면이 "표본이 적어 참고용"이라고 알려줍니다.',
      },
      {
        heading: '기억할 것',
        body:
          '전부 가상 계좌입니다. 실제 주문은 어떤 경우에도 나가지 않으며, ' +
          '여기서 검증된 방식을 실제 매매에 참고하는 것은 사용자의 판단입니다.',
      },
    ],
    glossary: [G_PROFIT, G_CUT, G_BAND_EXIT, G_BACKTEST, G_DRAWDOWN],
  },

  /*
    회차 이력 (2026-09-03 사용자 요청)

    🔴 계좌 탭이 아니라 기록을 읽는 탭이다
      다른 탭은 "이 계좌가 지금 어떻게 굴러가나" 를 보여준다.
      여기는 "지난 회차에 무슨 일이 있었나" 를 읽는 자리라 설명의 결이 다르다.
  */
  ROUNDS: {
    title: '📚 회차 이력',
    oneLiner: '지난 실험 회차의 성적과 AI가 쓴 회고·종합 분석을 다시 읽습니다.',
    settings: [
      { label: '무엇이 남나', value: '계좌별 회차 손익 · 청산 건수 · 넘긴 보유 · 회고 · 종합 분석' },
      { label: '보관 기간', value: '지우지 않습니다. 1회차 기록도 그대로 있습니다' },
      // ✏️ 9/28 I-78 2차 — 추천 3계좌는 날짜 단위로 자른다(PaperRoundHistoryService · sellDate between) — 저점매수만 시각 단위
      { label: '회차 구분', value: '회차 기간에 청산된 거래 (저점매수 · 스윙은 시작~종료 시각, 추천 계좌는 시작일~종료일 날짜 기준 · 스윙은 4회차부터 참여한 회차에만 줄이 생깁니다)' },
      { label: '회고를 쓰는 때', value: '2026년 10월 18일부터 멈췄습니다(3회차 결산 — 새 정보가 없었습니다). 그 전 회고는 그대로 남아 있습니다' },
      { label: '종합 분석을 쓰는 때', value: '회차가 끝난 날 21:30' },
    ],
    sections: [
      {
        heading: '왜 회차끼리 견줘야 하나',
        body:
          '한 회차는 3~4주짜리 표본이라 순위가 흔들립니다. 매매가 몇십 건일 때는 ' +
          '며칠 만에 1등이 바뀝니다. 과거 검증에서도 앞 1년의 1등이 뒤 1년에는 ' +
          '손실로 뒤집힌 적이 있습니다. 그래서 한 회차의 등수가 아니라 ' +
          '"같은 계좌가 회차를 넘어 꾸준한가"를 봐야 규칙을 바꿀 근거가 됩니다.',
      },
      {
        heading: '회차 성적은 어떻게 매기나',
        body:
          '2회차까지는 회차가 끝나면 보유를 전량 청산했으므로 확정된 손익(실현 손익)이 곧 성적이었습니다. ' +
          '3회차부터는 회차가 끝나도 보유를 팔지 않고 다음 회차로 넘깁니다 — 실제 계좌에는 회차 청산이 없고, ' +
          '2회차에는 회차 종료 청산이 청산의 약 70%를 차지해 성적을 흔들었기 때문입니다. ' +
          '그래서 회차가 끝나는 날 20:40에 그날 확정 종가로 평가한 금액을 성적표로 적고, ' +
          '성적 = 마감 평가액 ÷ 시작 평가액 − 1 로 매깁니다. 넘긴 보유의 평가 손익도 들어갑니다. ' +
          '진행 중인 회차를 펼치면 지금까지 판 것만 세므로, 들고 있는 종목까지 넣은 이번 회차 수익률은 ' +
          '비교 탭의 "이번 회차 수익률" 과 각 계좌 카드의 "이번 회차" 줄에서 봐야 합니다. ' +
          '회차가 끝난 뒤 다음 회차가 시작되기 전까지는 비교 탭의 순위도 이 성적표로 고정됩니다(제목 "N회차 최종 성적") — ' +
          '넘긴 보유 때문에 계좌 금액은 그 뒤에도 움직이지만 순위는 마감 성적으로 봅니다.',
      },
      {
        heading: '회차 번호가 거래에 적혀 있지 않다',
        body:
          '거래 기록에 회차 컬럼이 없어서, 회차의 시작·종료 시각으로 기간을 잘라 ' +
          '그 사이에 팔린 것을 그 회차의 것으로 봅니다(청산 건수 · 회고). 지난 회차에 산 종목을 ' +
          '이번 회차에 팔면 이번 회차의 청산입니다. 성적은 위의 성적표가 따로 정확히 잡습니다. ' +
          '컬럼을 새로 만들어 과거를 소급해 채우지 않은 것은, 소급이 틀리면 ' +
          '조용히 틀리고 나중에 알아챌 방법이 없기 때문입니다.',
      },
      {
        heading: '🔴 AI의 제안이 자동으로 반영되지는 않습니다',
        body:
          '종합 분석은 "재 볼 만한 것"을 제안하지만 그것으로 매매 규칙이 바뀌지 ' +
          '않습니다. 반영하려면 과거 데이터로 먼저 검증해야 하고, 실제로 검증해 보니 ' +
          '지금 방식이 더 나았던 경우가 있었습니다 — 익절 기준 완화, 추천 기준 완화, ' +
          '추세형 진입이 모두 검증에서 기각됐습니다. ' +
          '이력은 그 검증을 시작할 단서를 주는 자리입니다.',
      },
    ],
    glossary: [G_PROFIT, G_CUT, G_DRAWDOWN, G_BACKTEST],
  },
};

interface Props {
  tab: PaperTab | null;
  onClose: () => void;
}

export function ProfileHelpModal({ tab, onClose }: Props) {
  // 🔴 껍데기와 본문을 가른다
  //   본문이 저점매수 설정을 조회하는데, 여기서 부르면 도움말을 열지 않아도
  //   요청이 나간다. 훅은 early return 앞에 와야 하므로 컴포넌트를 나눈다.
  if (tab === null) {
    return null;
  }
  return <HelpBody tab={tab} onClose={onClose} />;
}

function HelpBody({ tab, onClose }: { tab: PaperTab; onClose: () => void }) {
  const content = HELP[tab];
  /*
    🔴 변형을 넘겨야 한다 (V119 · 챌린저는 2026-09-18)
      계좌마다 매수 기준가와 자금 규칙이 다른데, 변형 없이 부르면 다른 탭의 도움말이
      보수 계좌의 분위(20%)를 말한다 - 이 계좌들을 만든 이유가 바로 그 값이라
      틀린 설명 중에서도 가장 나쁜 종류다.
   */
  const bandVariantOf = (t: PaperTab) =>
    t === 'BAND_AGGRESSIVE'
      ? 'AGGRESSIVE'
      : t === 'BAND_CHALLENGER'
        ? 'CHALLENGER'
        : t === 'BAND_US_P20'
          ? 'US_P20'
          : 'CONSERVATIVE';
  const { data } = useBandStatus(bandVariantOf(tab));
  // 🔁 국내 스윙 탭이면 그 계좌 설정 · 섹터로 채운다 (2026-10-10). 다른 탭이면 부르지 않는다(enabled false)
  const swingKey = tab === 'SWING_A' || tab === 'SWING_C' || tab === 'SWING_US_A' ? tab : null;
  const { data: swing } = useSwingStatus(swingKey);
  // 🔎 스윙(6개월)만 저점 신호 검토 설정(깊은 단 · 정기 점검 · 하루 상한)을 읽는다 — 감시 표 칩 · 저점 신호 검토와 같은 queryKey(2026-10-18)
  const { data: aiReview } = useAiReview(AI_REVIEW_DAYS, tab === 'SWING_A');
  const settings =
    swingKey !== null
      ? swingSettings(swing?.config)
      : tab === 'BAND_US_P20'
        ? usBandSettings(data?.config)
        : tab === 'BAND' || tab === 'BAND_AGGRESSIVE' || tab === 'BAND_CHALLENGER'
          ? bandSettings(data?.config)
          : content.settings;
  // 저점매수 계열만 sectionsFor 를 갖는다. 본문의 감시 대상 수가 설정을 따라간다 (2026-09-12)
  const sections =
    swingKey !== null
      ? [
          ...swingSections(swing?.config, swing?.sectors.map((s) => s.name) ?? []),
          swingAiReviewSection(swingKey, swingKey === 'SWING_A' ? aiReview?.config : undefined),
        ]
      : content.sectionsFor === undefined
        ? content.sections
        : content.sectionsFor(data?.config);

  return (
    <Modal open onClose={onClose} maxWidth="sm:max-w-lg">
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-fg">{content.title}</h2>
            <p className="mt-1 text-sm text-fg-muted">{content.oneLiner}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="rounded p-1 text-fg-subtle hover:bg-surface-hover hover:text-fg-muted"
          >
            ✕
          </button>
        </div>

        {/*
          🔴 설정을 설명보다 먼저 보여준다 (2026-08-30 사용자 지시)
            "지금 어떤 값으로 돌고 있나" 를 모르면 화면의 결과를 해석할 수 없다.
            폰에서는 label 과 value 가 좁아 겹치므로 value 를 오른쪽 정렬하고
            줄바꿈을 허용한다.
        */}
        <section className="mt-4">
          <h3 className="text-sm font-semibold text-fg">지금 설정</h3>
          <dl className="mt-1.5 overflow-hidden rounded-lg border border-line">
            {settings.map((setting, index) => (
              <div
                key={setting.label}
                className={
                  'flex items-start justify-between gap-3 px-3 py-2 ' +
                  (index % 2 === 1 ? 'bg-surface-hover' : '')
                }
              >
                <dt className="shrink-0 text-xs text-fg-muted">{setting.label}</dt>
                <dd className="text-right text-xs font-medium text-fg">{setting.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-4 space-y-4">
          {sections.map((section) => (
            <section key={section.heading}>
              <h3 className="text-sm font-semibold text-fg">{section.heading}</h3>
              <p className="mt-1 text-sm leading-relaxed text-fg-muted">{section.body}</p>
            </section>
          ))}
        </div>

        {/* 용어는 맨 아래 - 필요한 사람만 내려와서 본다 */}
        <section className="mt-5 border-t border-line pt-4">
          <h3 className="text-sm font-semibold text-fg">용어</h3>
          <dl className="mt-2 space-y-2.5">
            {content.glossary.map((entry) => (
              <div key={entry.term}>
                <dt className="text-xs font-semibold text-fg">{entry.term}</dt>
                <dd className="mt-0.5 text-xs leading-relaxed text-fg-muted">{entry.meaning}</dd>
              </div>
            ))}
          </dl>
        </section>

        <p className="mt-4 border-t border-line pt-3 text-xs text-fg-subtle">
          모두 가상 계좌이며 실제 주문은 나가지 않습니다. 과거 데이터 검증(백테스트)
          결과는 미래 수익을 보장하지 않습니다.
        </p>
      </div>
    </Modal>
  );
}
